import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { ApiError, api } from '@/lib/api';
import { cx } from '@/lib/cx';
import { formatDateOnly } from '@/lib/format';
import { documentCustomerName } from '@/lib/company-names';
import { saveBlob } from '@/lib/download';
import { NAV_SURFACE_MODULE, futureBillablesDenialReason } from '@/lib/permissions';
import { KIND_NOUNS } from '@/lib/tracker-kind';
import { preferencesStore } from '@/lib/preferences-store';
import { useAuth } from '@/lib/use-auth';
import { useSavedFilterApply, useSavedFilters } from '@/lib/use-saved-filters';
import { useCurrencyOptions, currencyOptions } from '@/lib/use-invoice-options';
import { useTableFilters, type ColumnSpec } from '@/lib/use-table-filters';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
import { useColumnLayout, useFrozenOffsets } from '@/lib/use-column-layout';
import { useTrackerSheetLink } from '@/lib/use-tracker-sheet';
import { useCycleScopeIds } from '@/lib/use-line-project';
import {
  DEFAULT_PROJECT_CYCLE_SCOPE,
  PROJECT_CYCLE_SCOPES,
  PROJECT_CYCLE_SCOPE_LABELS,
  projectInCycleScope,
  projectOptionsByScope,
  projectScopeFootnote,
  type ProjectCycleScope,
} from '@/lib/project-cycle-scope';
import { isRealDate } from '@/components/catalog/form-utils';
import type {
  Project,
  InvoiceTrackerKind,
  InvoiceTrackerListParams,
  InvoiceTrackerPlan,
  InvoiceTrackerPlanCounts,
  InvoiceTrackerRow,
  InvoiceTrackerSort,
  InvoiceTrackerSource,
  InvoiceTrackerView,
  ListOrder,
  Paginated,
  UserSummary,
} from '@/lib/types';
import {
  Button,
  ColumnManager,
  ConfirmDialog,
  EmptyState,
  Field,
  FilterableTH,
  FilterSheet,
  Input,
  MultiCombobox,
  PageHeader,
  Panel,
  PanelFooter,
  PanelHeader,
  SegmentedControl,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import { fetchAllPages } from '@/lib/use-show-all';
import type {
  ComboboxOption,
  FilterableTHProps,
  FrozenSide,
  SegmentedControlOption,
} from '@/components/ui';
import { InlineField, type InlineSaveResult } from '@/components/ui/InlineField';
import { Notice } from '@/components/ui/Notice';
import { EmailLink, TextLink } from '@/components/ui/TextLink';
import { ViewToggle as ViewToggleStrip } from '@/components/ui/ViewToggle';
import { InvoiceMoney } from '@/components/invoices/InvoiceBits';
import { TrackerSheetPanel } from '@/components/tracker-sheet/TrackerSheetPanel';
import { FutureDocumentDialog } from '@/components/accounts-dashboard/FutureDocumentDialog';
import { PencilIcon, TrashIcon } from '@/components/catalog/icons';
import {
  CompletePlannedAction,
  PlanTabs,
  RaisedDocumentLink,
  ReopenPlannedAction,
} from '@/components/future-billables/FutureBillablePlanControls';

/*
 * THE FUTURE BILLABLES TRACKER — money that still has to be asked for.
 *
 * `GET /invoice-tracker` with `view: 'pending'` and `source: 'standalone'`
 * pinned. Those two are not controls and never become controls: **they are what
 * this page IS**, so they are sent explicitly on every request rather than left
 * to a server-side default (see `PINNED_VIEW` / `PINNED_SOURCE` below).
 *
 * ## It replaces `/invoice-tracker`, which was RETIRED — 2026-09-04
 *
 * The owner's decision, taken explicitly, and taken having been shown what it
 * costs: the old page's **Raised** view and its **quote- and invoice-attached**
 * rows have no screen in this app any more. That is deliberate, not an
 * oversight, and it is why nothing here offers a `view` or a `source` switch —
 * a control whose only outcome is a population this page does not serve would
 * be an affordance with one destination, which is the same rule that keeps
 * Xero's `sync` button off the quote panel.
 *
 * ⚠️ **The API is untouched.** `/invoice-tracker` still serves every view and
 * every source; only our screens over the other populations went. Do not read
 * the absence of a Raised view here as the endpoint having lost one.
 *
 * ## It is NOT a second copy of the Accounts dashboard's Future Billables
 *
 * `/accounts-dashboard` holds a section over exactly this row set and it STAYS.
 * The two are one table behind two questions: that section is one band of a
 * finance dashboard read beside quotes and invoices, this is the tracker over
 * the whole population with its own filters, its own paging and its own export.
 *
 * ⚠️ **The RAISE path is deliberately NOT duplicated here**, and its absence is
 * a decision rather than a gap. Raising turns a plan into a real quote or
 * invoice and pushes it to Xero, **which does not enforce invoice-number
 * uniqueness** — so a second implementation of that flow is a second way to
 * bill one customer twice, with nothing downstream refusing it. The Accounts
 * dashboard's Future Billables section owns it, complete with its
 * already-raised bookkeeping and its unlinked-document banner, and one
 * implementation of a money-creating action is worth more than two screens that
 * both offer it. **If raising is ever wanted here, MOVE that flow into a shared
 * component — do not write a second one.**
 *
 * ## What this page does write
 *
 * The tracker row's own lifecycle, and nothing that reaches a customer:
 * `comment` and the two planned-raise dates in place, and create / edit /
 * delete of the standalone row itself through `FutureDocumentDialog` — the same
 * dialog the Accounts dashboard uses, mounted rather than reimplemented.
 *
 * Since 2026-10-27 (owner) also the reversible **Completed** tick and its
 * **Reopen**, under two tabs on `?tab=` (Pending = absent, `completed`) that
 * send `plan` on the list and the export — see `PLAN_PARAM` and
 * `components/future-billables/FutureBillablePlanControls.tsx`. Neither raises
 * nor deletes anything; a row raised from a plan lands in Completed on its own
 * and links to its document from there.
 *
 * ## `key` is the identity, and `id` is never null HERE
 *
 * ⚠️ `InvoiceTrackerRow.id` is `number | null` across the tracker, because a
 * document nobody has typed against has no overlay row. **A standalone row is
 * the record**, so it always has one — but `key` is still what React and every
 * lookup here is keyed on, because that is true of every row in this type and a
 * page keyed on `id` breaks the moment the population widens.
 */

/** The server defaults `perPage`; the tracker is read a page at a time. */
const PER_PAGE = 50;
/** Fewer than a full page: enough to read as a list, not a wall of shimmer. */
const SKELETON_ROWS = 6;

/*
 * ⚠️ **THE TWO PINNED PARAMS, SENT ON EVERY REQUEST AND EXPORT.**
 *
 * `view: 'pending'` is money not yet asked for; `source: 'standalone'` narrows
 * that to the rows somebody TYPED, dropping the quotes awaiting an invoice that
 * `pending` also carries. **`source` is not a spelling of `view`** — neither can
 * be derived from the other, so both have to be said.
 *
 * ⚠️ **They are sent EXPLICITLY rather than relying on a default.** `raised` is
 * the server's default view and `source` has no default at all, so an omitted
 * key here would silently serve a different population under this page's title.
 * Typed as their contract unions rather than as bare strings, so a typo is a
 * compile error instead of a 422 that empties the page.
 */
const PINNED_VIEW: InvoiceTrackerView = 'pending';
const PINNED_SOURCE: InvoiceTrackerSource = 'standalone';

/* -------------------------------------------------------------------------
 * Table / Sheet — and the query key, which is the one thing here worth reading
 * --------------------------------------------------------------------- */

/**
 * 🔴 **THE KEY IS `?display=`, AND IT IS DELIBERATELY NOT `?view=`.**
 *
 * The other two trackers spell `table | sheet` on `?view=`. **This tracker
 * family already means something else by that key: a POPULATION.**
 * `InvoiceTrackerView` is `raised | pending`, `PINNED_VIEW` above is one of its
 * members, and the retired `/invoice-tracker` page's own `readView` read
 * anything that was not the literal `pending` as **`raised`** — so `?view=sheet`
 * was, and would be again, silently answered as the Raised table. **A link
 * meaning "show me the mirror" would land on a table with nothing failing**,
 * which is the failure this codebase records over and over: a wrong answer that
 * throws nothing, logs nothing and renders perfectly.
 *
 * ⚠️ **`?view=` is UNREAD on this page today, and that is not a licence to take
 * it.** `readFilters` below never touches it and `PINNED_VIEW` is a constant, so
 * claiming it would compile and work — until somebody opens a bookmark of
 * `/invoice-tracker?view=pending` that has been redirected here, or until the
 * population switch this page's header says it does not have is ever wanted
 * back. Two meanings on one key across three trackers is a key nobody can reason
 * about.
 *
 * **Why `display` cannot collide** (grepped across `src/`, 2026-09-04):
 * no page reads or writes it, it is not a `FilterKey` or a `SavedTrackerKey`
 * here, and it is not a member of `InvoiceTrackerListParams` — and it could not
 * reach the API even if it were, because `filterParams` below builds the request
 * key by key rather than forwarding the query string.
 *
 * ── THE TABLE IS THE DEFAULT, AND THAT IS A CHOICE ────────────────────────
 *
 * ⚠️ **`/client-deliverables` opens on its SHEET (owner, 2026-09-02) and this
 * page does NOT, deliberately.** The owner asked for the missing switcher, not
 * for a different landing view, and the two are separable: the table here is the
 * only view that filters, sorts, pages, exports and EDITS, and this mirror has
 * never been driven against a live Google document from this app. Opening onto
 * a frame that paints nothing — which a cross-origin iframe cannot report — would
 * make an unverified integration the first thing anybody sees. **Flipping the
 * default is one line and an owner's call; say so rather than assuming it.**
 *
 * So `sheet` is the value and the table is the key's ABSENCE: one canonical URL
 * per view, matching the `?view=board` opt-in on the four record pages. Nothing
 * is written into the URL on arrival — `table` needs no param to be expressed,
 * so there is no hidden default to fight a reader who clears one.
 */
const VIEW_PARAM = 'display';

/** The only recognised value. Anything else, and no param at all, is the table. */
const SHEET_VIEW = 'sheet';

type TrackerView = 'table' | 'sheet';

/**
 * The Pending / Completed tab (owner 2026-10-27), on `?tab=`. Pending is the
 * key's ABSENCE, the same shape as `?display=`: nothing is written on arrival,
 * and anything but the literal `completed` is Pending.
 *
 * ⚠️ **NOT a `FilterKey` and not saved.** Clearing filters must not throw the
 * reader back to Pending, and a remembered tab would make one link open two
 * different lists for two people. It travels as `plan` on the list and the
 * export; server side `plan` overrides the pinned `view` / `source`, which is
 * how the Completed tab also carries rows raised from a plan.
 */
// `plan`, never `tab`: the Operations Dashboard hosts this page and owns `tab`.
const PLAN_PARAM = 'plan';
const COMPLETED_PLAN: InvoiceTrackerPlan = 'completed';

function readPlan(params: URLSearchParams): InvoiceTrackerPlan {
  return params.get(PLAN_PARAM) === COMPLETED_PLAN ? 'completed' : 'pending';
}

const VIEW_OPTIONS: readonly { value: TrackerView; label: string }[] = [
  { value: 'table', label: 'Table' },
  { value: 'sheet', label: 'Sheet' },
];

/**
 * Two buttons in a group, copied from `ClientDeliverablesPage` rather than
 * invented beside it — which is also what keeps the two trackers' switchers
 * looking and behaving alike.
 *
 * `role="group"` with `aria-pressed`, NOT `role="tab"`: these switch what the
 * panel below IS, rather than labelling panels that share one tabstop. There is
 * no tab or segmented-control primitive in `src/components/ui` for this shape.
 *
 * ⚠️ **Both options render at every width, always.** This codebase has already
 * had a working feature reported as *deleted* when it had merely become
 * unreachable behind a view toggle — a missing affordance is indistinguishable
 * from a removed one. Nothing here collapses into a menu on a narrow screen.
 */
function ViewToggle({
  view,
  onChange,
}: {
  view: TrackerView;
  onChange: (next: TrackerView) => void;
}) {
  return (
    <ViewToggleStrip
      label="Future billables view"
      value={view}
      options={VIEW_OPTIONS}
      onChange={onChange}
    />
  );
}

/**
 * The sheet's place, held while the mirror's address is still being read.
 *
 * ⚠️ **Reached only when the reader has ASKED for the sheet.** The table is this
 * page's default and needs no network answer, so an ordinary arrival never waits
 * on one — unlike `/client-deliverables`, where the default view is the one that
 * has to. Here it exists so a `?display=sheet` link does not render the whole
 * table and swap it for the frame a moment later.
 *
 * The header is `TrackerSheetPanel`'s header, verbatim, so nothing moves when
 * the real panel takes over.
 *
 * ⚠️ **"Verbatim" IS AN INVARIANT NOTHING CHECKS, and it has been broken once
 * already on the other tracker** — the panel dropped a sentence and its
 * placeholder kept it, so for one edit the placeholder promised something the
 * frame that replaced it did not. **Change both in the same edit.**
 *
 * ⚠️ It is NOT rendered once the mirror turns out to be unconfigured — that is a
 * settled answer, not a pending one, and it lands on the table. A placeholder
 * resolving to nothing would be a shimmer promising a view that is never coming.
 */
function SheetPlaceholderPanel() {
  return (
    <Panel>
      <PanelHeader
        title="Google Sheets mirror"
        description="EMERGE rewrites this sheet automatically — edit the record in EMERGE, not here."
      />
      <div aria-busy className="border-b border-border p-4">
        <Skeleton className="h-[70vh] min-h-[360px] w-full" />
      </div>
    </Panel>
  );
}

/* -------------------------------------------------------------------------
 * The URL filters
 *
 * The list schema is `.strict()` across this API, so an unknown key is a 422
 * naming it and an EMPTY value is a 422 rather than "no filter". Everything here
 * is validated on the way OUT of the URL and DROPPED when it does not parse — a
 * pasted `?amountMin=abc` must be a slightly-wrong page, never a broken one.
 * --------------------------------------------------------------------- */

/**
 * ⚠️ **A DELIBERATELY SHORTER LIST THAN `/invoice-tracker` CARRIED, AND THE
 * OMISSIONS ARE THE REASONED HALF.**
 *
 * The old page offered `countryId`, `statusId`, `projectId`, `productId`,
 * `regionId` and the two bill-date bounds.
 *
 * 🔴 **THE PROJECT HALF OF THAT REASONING IS SUPERSEDED — 2026-09-17.**
 * *SUPERSEDED: "Every one of those narrows on a field that is **derived through a
 * document** — a country off the invoice, a payment status off
 * `f_invoice_statuses`, **a project** and a product off the document's line
 * items, a bill date off the invoice itself. **A standalone row has no
 * document**, so those columns are null or absent on every row this page can
 * ever hold, and a filter over them can only ever empty the table."*
 *
 * **A planned row carries its OWN project now** — `InvoiceTrackerRow.project`,
 * rendered as the Project column beside Services — so it is not derived through
 * a document and is not structurally absent here. **`?projectId=` matches BOTH
 * populations as of migration 215**: the clause is the existing document-line
 * `EXISTS` **OR** the row's own `project_id`, so one param answers "work for this
 * project" across raised documents and plans alike. `projectId` is therefore a
 * live filter on this page — see `ID_FILTER_KEYS` below.
 *
 * 🔴 **`productId` IS A LIVE FILTER HERE TOO — 2026-10-02, owner's decision.**
 * *SUPERSEDED 2026-10-02: "⛔ **`productId` IS STILL DELIBERATELY ABSENT, FOR
 * EXACTLY THE ORIGINAL REASON** … It still narrows through a document's LINE
 * ITEMS and a standalone row still has no document, so it still cannot match a
 * single row this page can hold. A planned row's basket lives in `products`,
 * which that param does not read."*
 *
 * `GET /invoice-tracker?productId=` was widened server-side to match a planned
 * row's OWN product basket (`products`, {@link InvoiceTrackerPlannedProduct}) as
 * well as a document's lines — the same OR shape `projectId` took in migration
 * 215. ⚠️ **If a product filter ever empties this page while rows visibly carry
 * that product, check the server's clause before this page** — a server
 * predating the widening matches lines only, which is the old reason above.
 * Choosing a product also NARROWS the Project dropdown to projects that sell it
 * (`/projects?productId=` is a membership filter).
 *
 * ⚠️ **The rest of the list is UNTOUCHED and the reasoning above still holds for
 * every other omission**: `countryId`, `statusId`, `regionId` and the bill-date
 * bounds are all still derived through a document, so a filter over any of them
 * can still only ever empty the table.
 *
 * ⚠️ **An empty table reads as "there is nothing planned", not as "your filter
 * matched nothing"** — which is why these are ABSENT rather than rendered and
 * disabled. A control whose only outcome is a blank page is the same shape as a
 * button whose only outcome is an error.
 *
 * ⚠️ **`financialYear` is omitted for the same reason and it is the one worth
 * saying out loud**, because it is the filter a reader of the old page would
 * expect most. The tracker's year narrows on the BILL DATE, which a planned row
 * does not have; the Accounts dashboard's section over this identical row set
 * sends no `financialYear` either. Adding one would open this page on a year
 * that excludes every row in it.
 */
const ID_FILTER_KEYS = ['companyId', 'projectId', 'productId'] as const;

const TEXT_FILTER_KEYS = ['currency', 'kind', 'amountMin', 'amountMax'] as const;

/**
 * Flag filters — present-or-absent rather than a value.
 *
 * A group of one, and it is still a group: `FILTER_KEYS` and `clearFilters` both
 * iterate it, so a second flag is one entry rather than a key somebody has to
 * remember to add in three places.
 */
const FLAG_FILTER_KEYS = ['plannedIn30Days'] as const;

const FILTER_KEYS = [...ID_FILTER_KEYS, ...TEXT_FILTER_KEYS, ...FLAG_FILTER_KEYS] as const;

type FilterKey = (typeof FILTER_KEYS)[number];

/** Ids, codes and flags are all held as strings: that is what the URL speaks. */
type Filters = Record<FilterKey, string>;

/**
 * What this page remembers against the account.
 *
 * ⚠️ **Every key is a FILTER and none is independent**, so the whole set travels
 * together: a link naming a company must not also impose the recipient's
 * remembered currency, or one address renders two different tables for two
 * people. The old tracker had `view` as its one independent key; this page has
 * no view to remember, which is why there is no `independentKeys` here.
 *
 * ⚠️ **The scope string is this page's own.** Reusing `'invoice-tracker'` would
 * hand this page the retired one's saved set — including keys it does not read,
 * which store and read back cleanly and do nothing.
 */
const SAVED_FILTER_KEYS = FILTER_KEYS;
type SavedTrackerKey = (typeof SAVED_FILTER_KEYS)[number];

/** `^[A-Z]{3}$` server-side — "usd" is a 422 rather than a case-insensitive match. */
const CURRENCY_PATTERN = /^[A-Z]{3}$/;
/** Money as text. Never `Number()` — a decimal string is exact and a float is not. */
const AMOUNT_PATTERN = /^\d+(\.\d{1,2})?$/;
/** The one spelling of an on flag in the URL. Anything else narrows nothing. */
const FLAG_ON = 'true';

/**
 * Every member of `InvoiceTrackerKind`, as the picker offers them.
 *
 * ⚠️ **DERIVED FROM `KIND_NOUNS`, NOT LISTED HERE.** *SUPERSEDED: "The TWO
 * members … `{ value: 'quote' }, { value: 'invoice' }`", a hand-written pair.*
 * A written list is a filter that silently stops offering a value the day the
 * union gains one — and it did, on 2026-09-11, when `'unknown'` landed. The
 * failure would have been invisible: the filter works, the column shows
 * "Not known yet" rows, and there is simply no way to ask for them.
 *
 * `Object.entries` over an exhaustive `Record` cannot go stale that way.
 */
const KIND_OPTIONS: readonly { value: InvoiceTrackerKind; label: string }[] = (
  Object.entries(KIND_NOUNS) as [InvoiceTrackerKind, string][]
).map(([value, label]) => ({ value, label }));

/**
 * ⚠️ *SUPERSEDED: `kind === 'invoice' ? 'Invoice' : 'Quote'`.* A two-arm
 * ternary labelled every "Not known yet" row as **Quote** the day the union
 * grew, and `tsc` could not see it. See `KIND_NOUNS` for why the shared map is
 * a `Record` rather than a function with a `default`.
 */
function kindLabel(kind: InvoiceTrackerKind): string {
  return KIND_NOUNS[kind];
}

/*
 * 2026-10-28 (owner): `companyId`, `projectId`, `productId`, `currency` and
 * `kind` are CSV lists — several values mean OR, the same param name, and a
 * single value still parses (old bookmarks and saved filters unchanged). Each
 * member is validated on its own and an invalid one is dropped, never the list.
 */
function splitCsv(raw: string): string[] {
  return raw === '' ? [] : raw.split(',').filter((value) => value !== '');
}

function csvOf(raw: string, valid: (value: string) => boolean): string {
  return Array.from(new Set(splitCsv(raw).filter(valid))).join(',');
}

const POSITIVE_ID = (value: string) => /^[1-9]\d*$/.test(value);

function readFilters(params: URLSearchParams): Filters {
  const amount = (key: 'amountMin' | 'amountMax') => {
    const raw = params.get(key) ?? '';
    return AMOUNT_PATTERN.test(raw) ? raw : '';
  };
  return {
    companyId: csvOf(params.get('companyId') ?? '', POSITIVE_ID),
    /*
     * The same positive-integer test `companyId` uses, for the same reason: the
     * list schema is `.strict()` and a pasted `?projectId=abc` would be a 422
     * emptying the page rather than a filter nobody set.
     */
    projectId: csvOf(params.get('projectId') ?? '', POSITIVE_ID),
    /* Same test, same reason: a pasted `?productId=abc` must narrow nothing. */
    productId: csvOf(params.get('productId') ?? '', POSITIVE_ID),
    currency: csvOf(params.get('currency') ?? '', (value) => CURRENCY_PATTERN.test(value)),
    /*
     * Narrowed against the contract union rather than passed through: `kind` is
     * a strict enum server-side, so a pasted `?kind=Quote` would be a 422
     * emptying the page rather than a filter nobody set.
     */
    kind: csvOf(params.get('kind') ?? '', (value) =>
      KIND_OPTIONS.some((option) => option.value === value),
    ),
    amountMin: amount('amountMin'),
    amountMax: amount('amountMax'),
    /*
     * Exactly `FLAG_ON` and nothing else: a URL carrying
     * `?plannedIn30Days=maybe` narrows nothing rather than 422ing the list and
     * reading as an outage.
     */
    plannedIn30Days: params.get('plannedIn30Days') === FLAG_ON ? FLAG_ON : '',
  };
}

/**
 * Written out key by key so a request can never carry a stray param, and so an
 * empty value is omitted entirely rather than emitted as a bare `key=`.
 *
 * ⚠️ **`amountMin` / `amountMax` stay STRINGS.** They are decimal text on the
 * wire for the same reason `amount` is: `Number('1234.10')` is exact today and
 * the arithmetic around it is not, and a rounded bound silently excludes the row
 * sitting on it. Do not "tidy" these into `Number(...)` the way `companyId` is.
 *
 * ⚠️ **`plannedIn30Days` goes out as the LITERAL `true` or not at all.**
 * `InvoiceTrackerListParams` types it as the literal precisely so `tsc` refuses
 * `Boolean(...)`: a `plannedIn30Days=false` on the query string is a 422 against
 * a `.strict()` schema, which empties the page rather than doing nothing.
 *
 * The return type is the API's own params interface, so a key this page invents
 * is a compile error rather than a 422.
 */
function filterParams(filters: Filters): InvoiceTrackerListParams {
  return {
    // CSV, already validated member by member in `readFilters`.
    ...(filters.companyId ? { companyId: filters.companyId } : {}),
    ...(filters.projectId ? { projectId: filters.projectId } : {}),
    ...(filters.productId ? { productId: filters.productId } : {}),
    ...(filters.currency ? { currency: filters.currency } : {}),
    ...(filters.kind ? { kind: filters.kind } : {}),
    ...(filters.amountMin ? { amountMin: filters.amountMin } : {}),
    ...(filters.amountMax ? { amountMax: filters.amountMax } : {}),
    ...(filters.plannedIn30Days ? { plannedIn30Days: true as const } : {}),
  };
}

/* -------------------------------------------------------------------------
 * Sorting
 * --------------------------------------------------------------------- */

/**
 * The sort keys THIS page offers — every funnelled column, sorted on the server.
 * *SUPERSEDED 2026-10-09: "two of the five the API accepts"* — backend `32057ed`
 * added a member for every remaining column.
 *
 * ⚠️ **`billDate`, `documentNumber` and `status` are omitted on purpose.** All
 * three are derived through a document and are null on every standalone row, so
 * a header offering them would claim an `aria-sort` for a control that reorders
 * nothing a reader can see. `FilterableTH` only claims `aria-sort` where the API
 * really sorts — the same rule, one step further in.
 */
const SORT_KEYS: readonly InvoiceTrackerSort[] = [
  'party',
  'amount',
  'kind',
  'services',
  'project',
  'currency',
  'plannedFrom',
  'plannedTo',
  'comment',
  'addedBy',
];

/**
 * ⚠️ **The default is `party` ASC, matching the Accounts dashboard's section
 * over this same row set.** An unrecognised `sort` is a 422 rather than a
 * fallback, so the column filters' bare string is NARROWED here rather than
 * cast.
 *
 * ⚠️ Both `sort` and `order` are sent on every request rather than left to the
 * server's own default. The default is not published, and a first render that
 * assumes one order while the response arrives in another is a table that
 * appears to re-sort itself on load.
 */
function asSortKey(key: string | undefined): InvoiceTrackerSort {
  return SORT_KEYS.includes(key as InvoiceTrackerSort) ? (key as InvoiceTrackerSort) : 'party';
}

/* -------------------------------------------------------------------------
 * Icons — Lucide paths, drawn by hand rather than pulling in a dependency.
 * --------------------------------------------------------------------- */

function SearchIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

function FilterIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <path d="M4 6h16" />
      <path d="M7 12h10" />
      <path d="M10 18h4" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <path d="M12 3v12" />
      <path d="m7 10 5 5 5-5" />
      <path d="M4 20h16" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </svg>
  );
}

/** A calendar with a forward arrow: work dated ahead of today. */
function FutureIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-5 w-5"
    >
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18" />
      <path d="M8 3v4" />
      <path d="M16 3v4" />
      <path d="M9 15.5h6" />
      <path d="m12.5 13 2.5 2.5-2.5 2.5" />
    </svg>
  );
}

function ChevronIcon({ direction }: { direction: 'left' | 'right' }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-3.5 w-3.5"
    >
      <path d={direction === 'left' ? 'm15 18-6-6 6-6' : 'm9 18 6-6-6-6'} />
    </svg>
  );
}

/* -------------------------------------------------------------------------
 * Filter option sources
 * --------------------------------------------------------------------- */

/**
 * Options for the company filter.
 *
 * `/companies` has a server-side `search`, so the Combobox debounces the typed
 * term straight into `setTerm` and the picker is never limited to the first
 * page's worth of names.
 */
/*
 * 🔴 *SUPERSEDED 2026-10-28: one page plus server-side `search`.* The filter is
 * a `MultiCombobox` now, which searches locally only, so every company is read
 * (paged by `fetchAllPages`) and the local search is exhaustive.
 */
function useCompanyFilterOptions() {
  const [options, setOptions] = useState<ComboboxOption[]>([]);
  const [failed, setFailed] = useState(false);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    fetchAllPages(
      (page, perPage, pageSignal) =>
        api.listCompanies({ page, perPage, sort: 'name', order: 'asc' }, pageSignal),
      signal,
    )
      .then((result) => {
        setOptions(
          result.rows.map((company) => ({ value: String(company.id), label: company.name })),
        );
        setTotal(result.total);
        setFailed(false);
      })
      .catch(() => {
        if (signal.aborted) return;
        setOptions([]);
        setFailed(true);
      })
      .finally(() => {
        if (!signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, []);

  return { options, loading, failed, total };
}

/** One stable empty list, so a loading render does not churn every memo downstream. */
const NO_PROJECTS: Project[] = [];

/**
 * Options for the project filter.
 *
 * 🔴 **`listAllProjects` — THE PAGING FETCH — AND NEVER `useProjectOptions`.**
 * `/projects` caps `perPage` at 100, which is the SERVER's own ceiling and not a
 * tuning knob, and there are **120 active projects** (2026-09-17). A
 * single-page read silently drops the tail of the alphabet, and because the cut
 * is name-sorted the tail is a real run of real projects rather than a random
 * few. That exact defect was reported by the owner as *"the projects are not
 * showing up"* — and on the line grids the picker came back **EMPTY rather than
 * merely short**, because every project selling the chosen product happened to
 * sit past the cut. **The fix for a cap is paging, never a bigger number.**
 *
 * ⛔ **NO `isActive: true`, AND THAT IS THE DIFFERENCE FROM THE DIALOG'S
 * PICKER.** A filter offers what can **MATCH**; a picker offers what can be
 * **CHOSEN**. A plan recorded against a project since retired is still a real
 * row somebody has to be able to find, so narrowing here would make it
 * unreachable — while `FutureDocumentDialog`, which is choosing a project for a
 * new plan, correctly asks for active ones only. **Do not copy that narrowing
 * into this hook.**
 *
 * ⚠️ **NO `onSearch`, deliberately — and it is the whole list that earns that.**
 * `Combobox` filters locally when no `onSearch` is given, so with every project
 * in hand the typed search is EXHAUSTIVE. Wiring server-side search here would
 * re-introduce a one-page read behind the search box, which is the failure mode
 * this hook exists to avoid: typing a project past the cap would find nothing
 * and read as that project having no planned work.
 *
 * ⚠️ **`listAllProjects` THROWS rather than resolving short**, so a failed page
 * discards the lot and this reports `failed` — a partial list would look
 * complete and quietly hide projects.
 *
 * 🔴 **A CHOSEN PRODUCT NARROWS THIS LIST — 2026-10-02, owner's decision.**
 * `productId` goes to `/projects` as a MEMBERSHIP filter, so the dropdown offers
 * only projects that sell it. It returns the raw `Project` rows rather than
 * options because the Current / Previous pills split them by `cycle`.
 *
 * ⚠️ **`names` survives a narrowing**: it accumulates every project name this
 * hook has read, so a project chosen BEFORE a product was picked — and absent
 * from the narrowed list — still renders by name when it is pinned, rather than
 * as a bare id. The filter is NOT cleared on the reader's behalf.
 */
function useProjectFilterOptions(productIds: string) {
  /*
   * 2026-10-28: the product filter is a CSV. Only ONE chosen product narrows
   * this list; with several the list is not narrowed (`/projects?productId=`
   * is not relied on to take a list).
   */
  const productId = POSITIVE_ID(productIds) ? productIds : '';
  /*
   * `settledFor` is the product the current rows answer for, so "loading" is
   * DERIVED (`settledFor !== productId`) rather than set at the top of the
   * effect — a stale list never reads as the answer for a newly chosen product.
   */
  const [result, setResult] = useState<{
    settledFor: string | null;
    rows: Project[];
    failed: boolean;
  }>({ settledFor: null, rows: [], failed: false });
  const [names, setNames] = useState<Map<string, string>>(() => new Map());

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    api
      /*
       * `sort` is load-bearing under paging rather than cosmetic: an unstable
       * order repeats rows across pages and skips others, so the list comes out
       * right in COUNT and wrong in CONTENT.
       */
      .listAllProjects(signal, {
        sort: 'name',
        order: 'asc',
        ...(productId ? { productId: Number(productId) } : {}),
      })
      .then((projects) => {
        if (signal.aborted) return;
        setResult({ settledFor: productId, rows: projects, failed: false });
        setNames((current) => {
          const next = new Map(current);
          for (const project of projects) next.set(String(project.id), project.name);
          return next;
        });
      })
      .catch(() => {
        if (signal.aborted) return;
        setResult({ settledFor: productId, rows: [], failed: true });
      });

    return () => controller.abort();
  }, [productId]);

  const loading = result.settledFor !== productId;
  return {
    rows: loading ? NO_PROJECTS : result.rows,
    names,
    loading,
    failed: !loading && result.failed,
  };
}

/**
 * One project row as a filter option.
 *
 * ⚠️ **A HINT ON AN OPTION, WHICH THE NO-HELPER-TEXT RULE DOES NOT REACH** — it
 * labels the option rather than explaining the control. It says why a project no
 * longer offered anywhere else is listed here: it can still MATCH rows, which is
 * this filter's whole job.
 */
function projectFilterOption(project: Project): ComboboxOption {
  return {
    value: String(project.id),
    label: project.name,
    ...(project.isActive ? {} : { hint: 'Retired' }),
  };
}

/**
 * The pill vocabulary, from the shared module's own labels — never spelled out
 * here, so this screen cannot call a set of projects something the next screen
 * calls something else.
 */
const PROJECT_CYCLE_SCOPE_OPTIONS: readonly SegmentedControlOption<ProjectCycleScope>[] =
  PROJECT_CYCLE_SCOPES.map((scope) => ({
    value: scope,
    label: PROJECT_CYCLE_SCOPE_LABELS[scope],
  }));

/**
 * Options for the product filter — every ACTIVE product, paged in whole.
 *
 * `listAllProducts` for the same reason the project hook pages: a single-page
 * read would drop the tail of the alphabet, and `Combobox` searches locally only
 * what it was handed. ⚠️ **A product's label is `productName`** — there is no
 * `name` key on a product row, and reading one renders a blank option.
 */
function useProductFilterOptions() {
  const [options, setOptions] = useState<ComboboxOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    api
      .listAllProducts(signal, { active: true })
      .then((products) => {
        if (signal.aborted) return;
        setOptions(
          products
            .map((product) => ({ value: String(product.id), label: product.productName }))
            .sort((a, b) => a.label.localeCompare(b.label)),
        );
        setFailed(false);
      })
      .catch(() => {
        if (signal.aborted) return;
        setOptions([]);
        setFailed(true);
      })
      .finally(() => {
        if (!signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, []);

  return { options, loading, failed };
}

/**
 * Keeps a selected filter renderable when its id is not in the loaded options.
 *
 * Three ways that happens: the id arrived on the URL before the options did, the
 * list is one page of a longer one, or the value is a row since deactivated — a
 * retired row still filters correctly server-side, but the option hooks list
 * active rows only.
 *
 * The loaded rows are the best label source available: every row matching a
 * `companyId` filter carries that company's name. With even that missing we show
 * the bare id rather than an empty trigger, which would read as "no filter"
 * while the list stayed filtered.
 */
function withSelected(
  options: ComboboxOption[],
  value: string,
  labels: Map<string, string>,
  noun: string,
): ComboboxOption[] {
  if (!value || options.some((option) => option.value === value)) return options;
  const known = labels.get(value);
  return [
    known
      ? { value, label: known }
      : { value, label: `${noun} #${value}`, hint: 'Not in the list below' },
    ...options,
  ];
}

/** `withSelected` for a CSV filter: every held value stays renderable. */
function withSelectedMany(
  options: ComboboxOption[],
  csv: string,
  labels: Map<string, string>,
  noun: string,
): ComboboxOption[] {
  return splitCsv(csv).reduce(
    (current, value) => withSelected(current, value, labels, noun),
    options,
  );
}

/**
 * One MULTI-value filter control (owner 2026-10-28): a `MultiCombobox` over a
 * CSV held in one URL param. Empty = all. No `hint` prop, as `FilterField`.
 */
function MultiFilterField({
  label,
  value,
  options,
  onChange,
  allLabel,
  loading,
  failed,
  searchPlaceholder,
  footnote,
  noun,
}: {
  label: string;
  /** The CSV as held in the URL. */
  value: string;
  options: ComboboxOption[];
  /** The next CSV; `''` clears the param. */
  onChange: (value: string) => void;
  allLabel: string;
  loading: boolean;
  failed: boolean;
  searchPlaceholder?: string;
  /** A FACT about the list, inside the popover — never advice. */
  footnote?: string;
  noun?: { singular: string; plural: string };
}) {
  return (
    <Field
      label={label}
      {...(failed ? { error: `Couldn’t load ${label.toLowerCase()} options.` } : {})}
    >
      {({ id, 'aria-describedby': describedBy }) => (
        <MultiCombobox
          id={id}
          values={splitCsv(value)}
          onChange={(next) => onChange(next.join(','))}
          options={options}
          placeholder={allLabel}
          loading={loading}
          searchPlaceholder={searchPlaceholder}
          maxVisibleChips={2}
          {...(noun ? { summaryNoun: noun } : {})}
          {...(footnote ? { footnote } : {})}
          aria-describedby={describedBy}
        />
      )}
    </Field>
  );
}

/*
 * 🔴 *SUPERSEDED 2026-10-28: `FilterField`, a single-value `Combobox`.* Every
 * picker filter here is a `MultiFilterField` now (owner: every filter takes
 * several values), so the single-value control went with its last mount.
 */

/** A plain text filter — the two amount bounds. */
function TextFilterField({
  label,
  value,
  onChange,
  inputMode,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  inputMode?: 'text' | 'decimal';
  placeholder?: string;
}) {
  return (
    <Field label={label}>
      {({ id, 'aria-describedby': describedBy }) => (
        <Input
          id={id}
          inputMode={inputMode}
          placeholder={placeholder}
          value={value}
          aria-describedby={describedBy}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </Field>
  );
}

/* -------------------------------------------------------------------------
 * The write half
 * --------------------------------------------------------------------- */

/**
 * The three keys edited IN PLACE on this page, and no others.
 *
 * ⚠️ Every payload built here is typed against this, structurally — so an
 * inline control cannot express a key it has no editor for even by accident.
 * The party, the services, the currency, the amount, the planned basket, **the
 * project** and the document kind are edited in `FutureDocumentDialog`, which is
 * one form with one Save; splitting them across inline fields as well would give
 * the same columns two writers.
 *
 * ⚠️ **The PROJECT is read-only here on purpose, and it is the one somebody will
 * reach for**, because the Project column sits between two cells that ARE inline
 * (the planned dates) and reads like a picker waiting to happen. A plan names one
 * project and its whole basket is for it — `plannedProductLines` puts that same
 * project on every line it seeds — so moving it is a change to what the basket
 * sells, not a field edit, and it belongs in the form that owns the basket.
 *
 * ⚠️ **`products` is deliberately not reachable from here.** It is a WHOLE-SET
 * REPLACE, so a payload builder that could reach it would eventually send `[]`
 * on an unrelated edit and wipe a basket somebody planned, with a 200 and
 * nothing to notice.
 */
interface FutureBillableTypedFields {
  comment?: string | null;
  plannedRaiseFrom?: string | null;
  plannedRaiseTo?: string | null;
}

/** `''` and `null` both clear an `optionalText` column. We send `null`, always. */
function textOrNull(next: string): string | null {
  return next.trim() === '' ? null : next;
}

/** `optionalDate()` — `''` and `null` both clear. We send `null`. */
function dateOrNull(next: string): string | null {
  return next === '' ? null : next;
}

/** The server's own limit, so the control stops where the API would. */
const COMMENT_MAX = 2_000;

/**
 * A part-typed date reads back from a native date control as `''`, which is
 * indistinguishable from a cleared field. Here clearing IS legal, so the only
 * thing to refuse is a complete value that is not a real calendar date —
 * `2026-02-30` sails through a regex and is refused by MySQL as a driver error.
 */
function validateDate(next: string): string | null {
  if (next === '') return null;
  return isRealDate(next) ? null : 'Enter a real date.';
}

interface TrackerRowWriter {
  canEdit: boolean;
  disabledReason: string | undefined;
  save: (
    requestKey: keyof FutureBillableTypedFields,
    patch: FutureBillableTypedFields,
  ) => Promise<InlineSaveResult>;
}

/**
 * Gating and the write, once per mounted row.
 *
 * ⚠️ **Gated on `ability.canUpdate('invoices')`, never on the role and never on
 * `/permissions/me` directly** — the same gate the retired tracker put on every
 * inline editor and the same one `FutureDocumentDialog` reads. A second spelling
 * of one question is how half a feature ends up on the other side of a
 * permission.
 *
 * ⚠️ **NOT `trackers-invoice`.** That module decides whether this SCREEN is
 * shown and the API enforces none of it; `invoices` is the module the API really
 * refuses a write under. Reading the view control here would tell somebody they
 * may write when the server will refuse them.
 *
 * The controls are mounted UNCONDITIONALLY and self-gate, so a reader without
 * the permission still sees every value with a sentence under it rather than an
 * absence. Hiding them would make a read-only account look like a tracker with
 * no comments in it.
 *
 * ⚠️ **PATCH only, with no upsert branch, and that is a property of this
 * page's population rather than of the endpoint.** A standalone row IS the
 * record, so it always carries an id; the `PUT` upsert exists for a DOCUMENT
 * nobody has typed against, and this page holds none. The `id === null` branch
 * is a refusal rather than an assertion, so a shape that arrives anyway disables
 * the control instead of writing against the wrong thing.
 */
function useTrackerRowWriter(
  row: InvoiceTrackerRow,
  onSaved: (next: InvoiceTrackerRow) => void,
): TrackerRowWriter {
  const { ability } = useAuth();
  // Operations may write future billables too — see `canWriteFutureBillables`.
  const canUpdate = ability.canWriteFutureBillables;
  const permissionReason = futureBillablesDenialReason(ability);
  const writable = row.id !== null;

  const canEdit = canUpdate && writable;
  const disabledReason = !canUpdate
    ? (permissionReason ?? 'Your account does not have permission to change this.')
    : !writable
      ? 'This row has no record behind it, so there is nothing to change.'
      : undefined;

  /*
   * A plain function, not a `useCallback`: it is only ever called from an
   * `InlineField`'s commit handler, which is a fresh closure every render
   * anyway, and memoising it would need `row` in a dependency list where a stale
   * one writes against the wrong id.
   */
  const save = async (
    requestKey: keyof FutureBillableTypedFields,
    patch: FutureBillableTypedFields,
  ): Promise<InlineSaveResult> => {
    const rowId = row.id;
    if (rowId === null) {
      return {
        status: 'error',
        message: 'This row has no record behind it, so there is nothing to change.',
      };
    }
    try {
      const response = await api.updateInvoiceTrackerRow(rowId, patch);
      /*
       * ⚠️ The record the SERVER returned replaces the one on screen — never a
       * local patch of the value we sent. The response carries every derived
       * field the write never saw, and on this row set that includes the amount,
       * which the planned basket can move.
       */
      onSaved(response.data);
      return { status: 'saved' };
    } catch (caught) {
      if (caught instanceof ApiError) {
        /*
         * A 403 carries no `details` at all, so it can never be a field error.
         * It can arrive even though `canUpdate` said otherwise, because a role
         * can change under a live access token — the gate above is the
         * optimisation, this branch is the guarantee.
         */
        if (caught.status === 403) {
          return {
            status: 'error',
            message: `${
              futureBillablesDenialReason(ability) ??
              'Your account no longer has permission to change this row.'
            } Nothing was saved.`,
          };
        }
        if (caught.status === 404) {
          return {
            status: 'error',
            message: 'This row is no longer on the tracker, so the change could not be saved.',
          };
        }
        /*
         * Split against the ONE key this save sent. A 422 whose path names it
         * belongs on this control; a `.strict()` rejection (`path: ''` plus a
         * `key`) or a path naming a key with no control here is unroutable, and
         * is reported as `warning` rather than `error`: the request is over and
         * the value did not take, so marking the draft invalid would state
         * something false about it.
         */
        const { fields, unroutable } = caught.routeFieldErrors([requestKey]);
        const fieldText = Object.values(fields).join(' ');
        if (fieldText !== '') return { status: 'error', message: fieldText };
        if (unroutable.length > 0) {
          return { status: 'warning', message: `${unroutable.join(' ')} Nothing was saved.` };
        }
        return { status: 'error', message: caught.message };
      }
      if (caught instanceof Error && caught.message !== '') {
        return { status: 'error', message: caught.message };
      }
      return {
        status: 'error',
        message: 'Could not reach the server. Check your connection and try again.',
      };
    }
  };

  return { canEdit, disabledReason, save };
}

/** Everything a field needs from its writer, so no call site gates one differently. */
function gate(writer: TrackerRowWriter) {
  return {
    disabled: !writer.canEdit,
    ...(writer.disabledReason === undefined ? {} : { disabledReason: writer.disabledReason }),
  };
}

/** The workbook's Comment column — free text, and the reason this page is editable. */
function CommentField({ row, writer }: { row: InvoiceTrackerRow; writer: TrackerRowWriter }) {
  return (
    <InlineField
      label="Comment"
      kind="multiline"
      value={row.comment ?? ''}
      maxLength={COMMENT_MAX}
      rows={3}
      display={
        row.comment && row.comment.trim() !== '' ? (
          /*
           * ⚠️ **Clamped to two lines with a `title`, NOT wrapped freely.** A
           * comment is the longest value on this table and an unbounded one sets
           * the height of its whole row while asking for width the columns
           * beside it then lose. The editor is unaffected: this is the READ
           * state, and opening it still shows the whole comment.
           */
          <span
            title={row.comment}
            className="line-clamp-2 text-xs text-fg-secondary [overflow-wrap:anywhere]"
          >
            {row.comment}
          </span>
        ) : undefined
      }
      onSave={(next) => writer.save('comment', { comment: textOrNull(next) })}
      {...gate(writer)}
    />
  );
}

/**
 * When the document is expected to go out.
 *
 * ⚠️ **TWO FIELDS, TWO WRITES.** One key per save: the server judges a lone date
 * against the stored other, so re-sending the unchanged half would re-assert a
 * value nobody touched. Do not fuse these into one control that patches both.
 */
function PlannedDateField({
  label,
  value,
  requestKey,
  writer,
}: {
  label: string;
  value: string | null;
  requestKey: 'plannedRaiseFrom' | 'plannedRaiseTo';
  writer: TrackerRowWriter;
}) {
  return (
    <InlineField
      label={label}
      type="date"
      value={value ?? ''}
      display={
        value ? (
          <span className="whitespace-nowrap text-xs text-fg-secondary tabular">
            {formatDateOnly(value)}
          </span>
        ) : undefined
      }
      validate={validateDate}
      onSave={(next) =>
        requestKey === 'plannedRaiseFrom'
          ? writer.save('plannedRaiseFrom', { plannedRaiseFrom: dateOrNull(next) })
          : writer.save('plannedRaiseTo', { plannedRaiseTo: dateOrNull(next) })
      }
      {...gate(writer)}
    />
  );
}

/* -------------------------------------------------------------------------
 * Cells
 * --------------------------------------------------------------------- */

/** Nothing recorded, rendered as a mark rather than as blank space. */
function NotRecorded() {
  return <span className="text-xs text-fg-subtle">—</span>;
}

/**
 * The Services column — the longest derived cell on the row.
 *
 * ⚠️ **Clamped to two lines with a `title` carrying the full text.** Unbounded
 * wrapping in an auto-layout table lets one long value set the height of a whole
 * row and take width from its neighbours. **Two lines rather than one**,
 * deliberately: this holds a list, and a one-line ellipsis on a list shows about
 * four words.
 *
 * ⚠️ `[overflow-wrap:anywhere]` is load-bearing beyond looks: it drops the
 * cell's min-content width to one character, which is what lets the
 * header-declared widths in `FUTURE_WIDTHS` actually hold.
 */
function ServicesCell({ services }: { services: string }) {
  if (services.trim() === '') return <NotRecorded />;
  return (
    <span
      title={services}
      className="line-clamp-2 text-xs text-fg-secondary [overflow-wrap:anywhere]"
    >
      {services}
    </span>
  );
}

/**
 * The project a planned row is for.
 *
 * ⛔ **`null` HAS TWO CAUSES AND NOTHING ON THE WIRE SEPARATES THEM**, so no copy
 * here may say "no project". It is EITHER an attached row — whose project lives
 * on the real document's line items rather than on the row — OR a plan nobody
 * has assigned one to. This page pins `source=standalone`, so in practice every
 * row it can hold is a plan; the field still cannot answer WHICH absence it is,
 * and a page-level fact is a poor reason to let a cell make a claim the data
 * does not support. It renders as the page's empty mark, the same treatment
 * `ServicesCell`, `AddedByCell` and the currency cell already give an absence.
 *
 * ⚠️ **ONE COMPONENT, TWO MOUNTS — the table cell and the sub-`lg` card.** The
 * two must not word an absence differently: a reader on a phone and a reader at
 * 1440 are looking at the same row, and a dash on one screen against a sentence
 * on the other reads as two different facts.
 *
 * ⚠️ **`truncate` rather than `ServicesCell`'s two-line clamp**, because this is
 * a single value and not a list — but `truncate` is `white-space: nowrap`, so
 * the cell's min-content width becomes the WHOLE name. The `max-w` in
 * `rowCellFor` is what stops it widening its column past the declared width and
 * squeezing every other one; the two move together.
 *
 * ⚠️ **NOT A LINK, and that is this change's scope rather than a ruling.** The
 * name is what the column was asked for; `/projects/:id` exists and linking it
 * the way `party` links a company is a reasonable later change, not one to make
 * silently on the way past.
 */
function ProjectCell({ project }: { project: { id: number; name: string } | null }) {
  if (!project) return <NotRecorded />;
  return (
    <span title={project.name} className="block truncate text-xs text-fg-secondary">
      {project.name}
    </span>
  );
}

/**
 * The one spelling of a staff member's display name on this page.
 *
 * ⚠️ **`name` COMES BACK `''` ON REAL RECORDS**, so `person.name` bare prints an
 * empty slot — which reads as a broken record rather than as a missing one. The
 * fallback runs name → email → `Staff #<id>`, and the last rung cannot be empty,
 * so this always returns something to render.
 *
 * ⚠️ **The column's `get` calls this too, and must.** The funnel groups on what
 * `get` returns, so a cell that formatted the name differently from the filter
 * would offer options that match nothing the reader can see.
 */
function personLabel(person: UserSummary): string {
  return person.name.trim() || person.email.trim() || `Staff #${person.id}`;
}

/**
 * Who added this planned row — the whole point of the column being here, which
 * is that Accounts has to go and ask that person about the money.
 *
 * ⚠️ **THE EMAIL IS THE POINT, NOT DECORATION.** A name alone answers "who" and
 * leaves the reader to go and look them up; the address is what makes the column
 * actionable. It is rendered as a second line AND carried on the cell's `title`
 * with the name, because either line can truncate at this width — a `title`
 * carrying a value a cell cannot fit is the established treatment here and is
 * explicitly not helper text.
 *
 * ⛔ **`undefined` AND `null` BOTH RENDER AS THE EMPTY MARK, AND NEITHER MAY BE
 * NAMED.** `undefined` is a server older than the field (see
 * {@link InvoiceTrackerRow.createdBy} — it was not on the wire when this
 * shipped). `null` is EITHER a row whose author's account has since been deleted
 * OR a row nothing ever stamped, and nothing on the wire separates the two —
 * so "Unknown" and "System" are both claims this app cannot support.
 */
function AddedByCell({ person }: { person: UserSummary | null | undefined }) {
  if (!person) return <NotRecorded />;

  const label = personLabel(person);
  const email = person.email.trim();
  // Not `label + email`: where the name was empty the label IS the email, and
  // repeating it in the title prints the address twice.
  const full = email === '' || email === label ? label : `${label} · ${email}`;

  return (
    <span title={full} className="block min-w-0">
      <span className="block truncate text-xs text-fg-secondary">{label}</span>
      {email === '' || email === label ? null : (
        <span className="mt-0.5 block truncate text-2xs text-fg-muted">
          <EmailLink address={email} tone="muted" className="[overflow-wrap:normal]" />
        </span>
      )}
    </span>
  );
}

/* -------------------------------------------------------------------------
 * Row actions
 * --------------------------------------------------------------------- */

/**
 * Corrects a planned row through the same dialog that created it.
 *
 * ⚠️ **NOTHING HERE CREATES A DOCUMENT**, which is why it is gated far more
 * loosely than the raise flow on the Accounts dashboard. It edits the tracker's
 * own row and can produce nothing a customer ever sees.
 *
 * ⚠️ **ONLY A STANDALONE ROW IS EDITABLE, AND THE ACTION IS ABSENT RATHER THAN
 * DISABLED ON ANY OTHER.** A row attached to a real quote or invoice reads its
 * party, currency, amount and basket THROUGH that document, and the server
 * refuses every one of those keys against it — so there would be nothing for the
 * dialog to write. That refusal is about the SYSTEM and identical for everybody,
 * which is the case where an affordance whose only outcome is an error must not
 * be shown at all. The permission branch below is the opposite case: it is about
 * THIS READER, so it stays visible and says why.
 *
 * ⚠️ **Belt and braces today**: `source: 'standalone'` is pinned on the request,
 * so every row this page holds already passes. It is tested here anyway because
 * that is where the rule belongs.
 */
function EditPlannedAction({
  row,
  disabled,
  onOpen,
}: {
  row: InvoiceTrackerRow;
  disabled: boolean;
  onOpen: () => void;
}) {
  const { ability } = useAuth();
  const canUpdate = ability.canWriteFutureBillables;
  const reason = futureBillablesDenialReason(ability);

  if (row.source !== 'standalone') return null;

  return (
    <Button
      size="sm"
      variant="ghost"
      iconOnly
      leadingIcon={<PencilIcon />}
      aria-label={`Edit: ${row.partyName}`}
      /*
       * ⚠️ **DISABLED WITH A SENTENCE, NEVER HIDDEN.** A reader who may not write
       * cannot tell a gated button from a broken one, and this page has no other
       * way of telling them. Icon-only since 2026-10-27 (owner): the tooltip is
       * the name, or the reason when withheld.
       */
      tooltip={
        canUpdate ? 'Edit' : (reason ?? 'You do not have permission to change planned documents.')
      }
      disabled={!canUpdate || disabled}
      onClick={onOpen}
    />
  );
}

/**
 * ⚠️ **THE ROW IS ALL THERE IS — SO THIS IS A REAL LOSS WITH NO UNDO.** A
 * standalone row carries its own party, services, currency, amount, planned
 * basket, planned window and comment, all typed by a person and read back from
 * nothing. Deleting it destroys every one of them; there is no document behind
 * it to re-derive from and no server-side 409 to catch a mistake. Hence the
 * confirmation NAMES the party and SAYS what goes, rather than asking whether
 * the user is sure.
 *
 * ✅ **Gated on `canWriteFutureBillables` since 2026-09-28** — owner's decision:
 * the DELETE route takes `FUTURE_BILLABLE_WRITE_ROLES`, the same list as add and
 * edit, so Operations (`viewer`) may delete. *SUPERSEDED: "`ADMIN_ROLES` on the
 * server, so `canDelete`, never `canUpdate`."*
 */
function DeletePlannedAction({
  row,
  disabled,
  onDeleted,
}: {
  row: InvoiceTrackerRow;
  disabled: boolean;
  onDeleted: (key: string) => void;
}) {
  const { ability } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rowId = row.id;
  if (row.source !== 'standalone' || rowId === null) return null;

  const canDelete = ability.canWriteFutureBillables;
  const reason = futureBillablesDenialReason(ability);

  /*
   * An arrow bound after the guard above, deliberately not a `function`
   * declaration: a declaration is HOISTED, so TypeScript treats it as created
   * before `rowId` was narrowed and hands the call a `number | null`. A cast
   * would assert exactly the fact the guard two lines up already proved, on the
   * argument that decides which row is destroyed.
   */
  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.deleteInvoiceTrackerRow(rowId);
      setOpen(false);
      onDeleted(row.key);
    } catch (caught) {
      if (caught instanceof ApiError) {
        if (caught.status === 403) {
          setError(
            `${reason ?? 'Your account no longer has permission to delete this row.'} Nothing was deleted.`,
          );
        } else if (caught.status === 404) {
          /*
           * Already gone — somebody else deleted it, or the page has been open a
           * while. The row leaves the table rather than reporting a failure for
           * an outcome that is exactly what was asked for.
           */
          setOpen(false);
          onDeleted(row.key);
          return;
        } else {
          setError(`${caught.message} Nothing was deleted.`);
        }
      } else {
        setError(
          caught instanceof Error
            ? `${caught.message} Nothing was deleted.`
            : 'Could not reach the server. Nothing was deleted.',
        );
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        size="sm"
        variant="ghost"
        iconOnly
        leadingIcon={<TrashIcon />}
        aria-label={`Delete: ${row.partyName}`}
        tooltip={canDelete ? 'Delete' : (reason ?? 'You do not have permission to delete this row.')}
        className="text-fg-subtle hover:text-danger"
        disabled={!canDelete || disabled}
        onClick={() => {
          setError(null);
          setOpen(true);
        }}
      />
      <ConfirmDialog
        open={open}
        onClose={() => {
          if (!busy) setOpen(false);
        }}
        onConfirm={() => void confirm()}
        title="Delete this planned document?"
        confirmLabel={busy ? 'Deleting…' : 'Delete row'}
        loading={busy}
        {...(error === null ? {} : { error })}
        description={
          <>
            <p className="m-0">
              This deletes the planned {kindLabel(row.kind).toLowerCase()} for{' '}
              <strong>{row.partyName}</strong>, and the services, currency, amount, planned
              products, planned dates and comment recorded on it go with it. Nothing else holds
              those values, and there is no undo.
            </p>
            <p className="m-0 mt-2">
              No quote or invoice is affected — this row has neither behind it.
            </p>
          </>
        }
      />
    </>
  );
}

/* -------------------------------------------------------------------------
 * Columns
 * --------------------------------------------------------------------- */

/**
 * ⚠️ **THE COLUMN SET IS THE ACCOUNTS DASHBOARD'S FUTURE BILLABLES SET, NOT THE
 * RETIRED TRACKER'S.** That is deliberate: the two pages read one row set, so a
 * column here that is not there — or the reverse — is one screen describing the
 * same money differently. The retired tracker's `Invoice #`, `Bill date`,
 * `Export/Local` and `Payment status` are all derived through a document and are
 * empty on every row this page holds.
 *
 * Every column is `scope: 'page'`: the server filters are driven by the URL-
 * backed bank above the table, and declaring one on a header too would give one
 * query param two uncoordinated controls.
 *
 * Every column carries a `sortKey` since 2026-10-09 (backend `32057ed`) — see
 * `SORT_KEYS`. *SUPERSEDED: "`sortKey` appears only on the two columns the API
 * can order by".* Each is pinned with `satisfies InvoiceTrackerSort`, so a key
 * the list schema does not declare fails `tsc` rather than coming back a 422.
 */
const FUTURE_COLUMNS: ColumnSpec<InvoiceTrackerRow>[] = [
  /*
   * ⚠️ `format` reads the RAW value rather than calling `kindLabel(row.kind)` in
   * `get`: the funnel groups on what `get` returns, so labelling there would
   * make the filter's own options a translation of the wire value rather than
   * the value itself. `format` is exactly the hook for that — a nicer label over
   * an unchanged key.
   */
  {
    key: 'kind',
    label: 'Type',
    get: (row) => row.kind,
    format: (value) => (value === 'invoice' ? 'Invoice' : 'Quote'),
    sortKey: 'kind' satisfies InvoiceTrackerSort,
  },
  /*
   * ⚠️ **THE LABEL IS "Company name"; `key` AND `sortKey` ARE STILL `party`.**
   * `party` is a member of `InvoiceTrackerSort` and the list schema is
   * `.strict()`, so renaming the key to match the label would send
   * `sort=company` and come back a 422 that empties the whole table — a dead
   * list reads as "nothing is planned", not as a bad control.
   */
  {
    key: 'party',
    label: 'Company name',
    get: (row) => rowPartyLabel(row),
    sortKey: 'party' satisfies InvoiceTrackerSort,
  },
  {
    key: 'services',
    label: 'Services',
    get: (row) => row.services,
    sortKey: 'services' satisfies InvoiceTrackerSort,
  },
  /*
   * Immediately after Services, because the two answer adjacent questions —
   * what is being sold, and what it is for. Reading them apart is what makes a
   * basket legible; a project column at the far end of a scrolling table is one
   * a reader has to go and find.
   *
   * ✅ **SORTED ON THE SERVER since 2026-10-09** (`project`, backend `32057ed`).
   * *SUPERSEDED: "⚠️ **NO `sortKey`.** `InvoiceTrackerSort` is
   * `billDate | party | amount | documentNumber | status`"* — the member exists
   * now, so the header sorts the whole list rather than nothing.
   *
   * ⚠️ **`get` returns the NAME, which is exactly what the cell shows.** The
   * funnel groups on what `get` returns, so returning `row.project` or its id
   * would give the popover values no reader could match to a row — the same rule
   * `addedBy` states below.
   *
   * ⚠️ **`null` reaches the funnel as an empty option, and that is honest here.**
   * It cannot be labelled, because `row.project` is `null` for two different
   * reasons and the field cannot tell them apart — see `ProjectCell`.
   */
  {
    key: 'project',
    label: 'Project',
    get: (row) => row.project?.name ?? null,
    sortKey: 'project' satisfies InvoiceTrackerSort,
  },
  {
    key: 'currency',
    label: 'Currency',
    get: (row) => row.currency,
    sortKey: 'currency' satisfies InvoiceTrackerSort,
  },
  {
    key: 'amount',
    label: 'Amount',
    // The raw decimal STRING, never `Number(row.amount)`. The funnel groups
    // exact values, and a float round-trip is how two identical figures stop
    // being the same option.
    get: (row) => row.amount,
    numeric: true,
    sortKey: 'amount' satisfies InvoiceTrackerSort,
  },
  {
    key: 'plannedFrom',
    label: 'Planned from',
    get: (row) => row.plannedRaiseFrom,
    format: formatDateOnly,
    sortKey: 'plannedFrom' satisfies InvoiceTrackerSort,
  },
  {
    key: 'plannedTo',
    label: 'Planned to',
    get: (row) => row.plannedRaiseTo,
    format: formatDateOnly,
    sortKey: 'plannedTo' satisfies InvoiceTrackerSort,
  },
  {
    key: 'comment',
    label: 'Comment',
    get: (row) => row.comment,
    sortKey: 'comment' satisfies InvoiceTrackerSort,
  },
  /*
   * ✅ **SORTED ON THE SERVER since 2026-10-09** (`addedBy`, backend `32057ed`).
   * *SUPERSEDED: "⚠️ **NO `sortKey`.** `InvoiceTrackerSort` has no member for
   * the creator"* — it does now.
   *
   * ⚠️ **`get` returns the SAME string the cell shows** (see `personLabel`), so
   * the funnel's options are the names on screen. Returning `row.createdBy` or
   * an id would give the popover values no reader could match to a row.
   */
  {
    key: 'addedBy',
    label: 'Added by',
    get: (row) => (row.createdBy ? personLabel(row.createdBy) : null),
    sortKey: 'addedBy' satisfies InvoiceTrackerSort,
  },
];

const FUTURE_COLUMN_KEYS: readonly string[] = FUTURE_COLUMNS.map((column) => column.key);

/**
 * The Completed tab's two extra columns (owner 2026-10-27): when, and by whom.
 *
 * ⚠️ **NO `sortKey`.** `InvoiceTrackerSort` has no member for either, so a
 * header offering a sort would claim an order the API does not apply.
 *
 * ⚠️ **Shown on the Completed tab only** — every Pending row has `null` in both,
 * so on that tab they would be two columns of dashes. The Completed tab keeps
 * its own column layout (`COMPLETED_COLUMN_SCOPE`) so neither tab's manager
 * lists a column it does not draw.
 */
const COMPLETED_COLUMNS: ColumnSpec<InvoiceTrackerRow>[] = [
  {
    key: 'completedOn',
    label: 'Completed on',
    get: (row) => row.completedAt,
    format: formatDateOnly,
  },
  {
    key: 'completedBy',
    label: 'Completed by',
    get: (row) => row.completedBy?.name ?? null,
  },
];

const COMPLETED_COLUMN_KEYS: readonly string[] = [
  ...FUTURE_COLUMN_KEYS,
  ...COMPLETED_COLUMNS.map((column) => column.key),
];

/** Every spec either tab can draw — what `useTableFilters` is given. */
const ALL_COLUMNS: ColumnSpec<InvoiceTrackerRow>[] = [...FUTURE_COLUMNS, ...COMPLETED_COLUMNS];

const FUTURE_COLUMN_BY_KEY: ReadonlyMap<string, ColumnSpec<InvoiceTrackerRow>> = new Map(
  ALL_COLUMNS.map((column) => [column.key, column]),
);

/**
 * The row-actions lane, as a column key.
 *
 * ⚠️ **IT IS NOT A `ColumnSpec` AND MUST NOT BECOME ONE.** It carries no
 * filterable value, so giving it a funnel would offer to filter on a button —
 * which is why `FUTURE_COLUMN_KEYS` (what the filter sheet and the header
 * funnels are driven by) deliberately does not contain it. It appears here only
 * so the column manager can LIST it, disabled: a lane missing from that list
 * reads as a lane the feature lost.
 */
const ACTIONS_KEY = 'actions';

/**
 * The layout scope — the same string the saved-filter scopes use, so one page's
 * columns cannot land in another's branch of `preferences.columns`.
 */
const COLUMN_SCOPE = 'future-billables';

/** The Completed tab's own layout scope — see `COMPLETED_COLUMNS`. */
const COMPLETED_COLUMN_SCOPE = 'future-billables-completed';

/** Every key the manager lists, in DECLARED order. Actions is last and stays last. */
const LAYOUT_KEYS: readonly string[] = [...FUTURE_COLUMN_KEYS, ACTIONS_KEY];

/** The same for the Completed tab, with its two extra columns before Actions. */
const COMPLETED_LAYOUT_KEYS: readonly string[] = [...COMPLETED_COLUMN_KEYS, ACTIONS_KEY];

/**
 * ⛔ **THE ACTIONS LANE MAY NOT BE HIDDEN OR MOVED.** Edit and Delete have no
 * second home on this table — unlike the sub-`lg` cards, which carry their own
 * `RowActions` — so hiding the lane would remove the only way to reach either,
 * and a missing affordance is indistinguishable from a removed feature. That is
 * the `/companies` board incident, where an unreachable "+" menu was reported as
 * a deleted feature.
 *
 * ⚠️ *SUPERSEDED: "Its two FREEZE toggles in the manager are the one inert pair
 * on this page … Suppressing the buttons would mean editing the shared
 * `ColumnManager`, which is not this file's to change."* **`ColumnManager` takes
 * `pinnedKeys` now and omits the freeze controls for those keys**, so the inert
 * pair is gone — see `PINNED_KEYS` below. The reasoning that made them inert is
 * unchanged and is why the lane is pinned at all.
 */
const LOCKED_KEYS: readonly string[] = [ACTIONS_KEY];

/**
 * The lane the table renders `pinned` — which IS a right-edge freeze at offset
 * 0, as `Table.tsx` says at `FrozenSide`.
 *
 * Declaring it buys three things that each used to be hand-rolled here:
 *
 * - **The manager omits its freeze controls.** Absent rather than disabled:
 *   that edge is not the reader's to choose at all, so there is no state a
 *   disabled control would explain. Honouring freeze-LEFT instead would float
 *   the row buttons over the company name, which is the failure `Table.tsx`
 *   records against the right edge.
 * - **`setFrozen` refuses the key**, so a stored freeze can never sit alongside
 *   the `pinned` class and leave two conflicting position rules on one cell.
 * - 🔴 **`useFrozenOffsets` COUNTS the lane's width.** *SUPERSEDED: a local
 *   `frozenSideOf` wrapper answering `'right'` for this key alone.* Without the
 *   lane in that sum the next right-frozen column is handed an offset of
 *   **zero** — the same edge — and the two stack, each fully opaque, so one
 *   simply disappears. Nothing errors.
 *
 * ⚠️ **NOT the same set as `LOCKED_KEYS`**, even though they happen to hold the
 * same key here. Locked is about hiding and reordering; pinned is about which
 * edge a cell already occupies. Conflating them is what made an identifying
 * column unfreezable on the pages that lock one.
 */
const PINNED_KEYS: readonly string[] = [ACTIONS_KEY];

/**
 * Why the locked lane is locked, in the reader's own terms.
 *
 * ⚠️ **The manager's fallback sentence — "This column cannot be hidden" — names
 * the rule and not the reason**, and a lock has several possible reasons. This
 * one is about reachability: Edit and Delete have no second home on this table.
 * A **why-this-is-disabled** sentence is one of the four kinds the
 * no-helper-text prohibition explicitly keeps.
 */
function columnLockReasonOf(key: string): string | undefined {
  return key === ACTIONS_KEY ? 'The row actions live only in this column' : undefined;
}

/** The manager's label for a key — the same string its header shows. */
function columnLabelOf(key: string): string {
  return key === ACTIONS_KEY ? 'Actions' : (FUTURE_COLUMN_BY_KEY.get(key)?.label ?? key);
}

/**
 * The width every column asks for, keyed by `ColumnSpec.key`.
 *
 * ⚠️ **A `<th>` WIDTH IS A REQUEST, NOT A RULE, AND THAT IS WHY IT WORKS HERE.**
 * `Table` carries no `table-fixed`, so auto layout honours a declared width
 * unless a cell's MIN-CONTENT demands more. The free-text cells are
 * `[overflow-wrap:anywhere]`, whose min-content is one character, so nothing
 * forces a column past what it asks for — the property `table-fixed` would buy,
 * without its cost of clipping the `whitespace-nowrap` money and date cells.
 *
 * ⚠️ **THE NUMBERS ARE FLOORS SET BY THE HEADER LABEL, not by the data.** A
 * header is `text-2xs` uppercase with `tracking-[0.06em]`, plus a funnel button
 * (~20px), its gap (4px) and `TH`'s `px-3` (24px) — so a column narrower than
 * its own label is a heading truncated mid-word. Check the LABEL first when
 * adding a column, then widen for content.
 *
 * ⚠️ **`min-w` ON THE TABLE IS THE SUM OF THE VISIBLE ONES PLUS THE ACTIONS
 * LANE, AND IT IS NOW COMPUTED RATHER THAN WRITTEN OUT.** It used to be the
 * literal `min-w-[1608px]` (`96 + 200 + 216 + 116 + 136 + 152 + 144 + 240 + 184
 * = 1484, + 124 actions`), which was correct while every column always
 * rendered. **Columns can be hidden now**, and a fixed floor over a narrowed
 * table reserves room for columns that are not there — the reader gets a
 * horizontal scrollbar with nothing at the end of it, on a table that would
 * otherwise have fitted.
 *
 * ⛔ **THE SUM STILL MAY NOT BECOME A CLASS.** Tailwind scans SOURCE for class
 * names, so a computed `min-w-[${n}px]` produces no CSS at all and fails
 * silently — the worst outcome available here, because the class lands in the
 * DOM and looks applied. The number goes through `style={{ minWidth }}`, which
 * is the same answer `Table.tsx` gives for a frozen column's offset.
 *
 * ⚠️ **THE PX FIGURES ARE PARSED BACK OUT OF THESE CLASS STRINGS, deliberately,
 * so there is ONE list rather than two.** A parallel `Record<string, number>`
 * would agree the day it was written and drift on the first column resized —
 * and drift here is silent, because a wrong sum only ever squeezes or
 * over-reserves rather than erroring. `widthPx` is the parser; a class it
 * cannot read contributes 0, which under-reserves (columns squeeze) rather than
 * over-reserving, so the failure stays visible on screen instead of hiding
 * behind a scrollbar.
 *
 * ⚠️ **TWO PLACES STILL MOVE TOGETHER WHEN A COLUMN IS ADDED**: the entry here
 * and the cell's own `max-w` in `rowCellFor`. A `max-w` wider than the `w-[…]`
 * quietly re-opens the squeeze the two exist to prevent.
 */
const FUTURE_WIDTHS: Readonly<Record<string, string>> = {
  kind: 'w-[96px]',
  party: 'w-[200px]',
  services: 'w-[216px]',
  /*
   * Sized for the NAME, not for the header label — "Project" is one of the
   * shortest labels on this table and a real project reads
   * `Business School Festival - India - Summer 2026`. Matched to `party` because
   * both hold a single record's name and both truncate to one line with the
   * whole value on a `title`; sizing it like `services` beside it would reserve
   * list-sized room for a single value.
   */
  project: 'w-[200px]',
  currency: 'w-[116px]',
  amount: 'w-[136px]',
  plannedFrom: 'w-[152px]',
  plannedTo: 'w-[144px]',
  comment: 'w-[240px]',
  /*
   * Sized for the EMAIL rather than the name — `accounts@seedglobaleducation.com`
   * is the long line in this cell, and the header label "Added by" is far
   * shorter than either. Both lines truncate with the whole thing on the cell's
   * `title`, so this is a comfortable width and not a guarantee.
   */
  addedBy: 'w-[184px]',
  completedOn: 'w-[136px]',
  completedBy: 'w-[160px]',
};

/**
 * The reserved lane for the row actions — see the sum above.
 *
 * ⚠️ Declared rather than left to the content: the lane carries Edit and Delete,
 * and either can be withheld on a row. Undeclared, the column would change width
 * with whichever rows the pager happened to land on, moving every column left of
 * it.
 */
const FUTURE_ACTIONS_WIDTH = 'w-[132px]';

/**
 * The px figure inside a `w-[…px]` class, or 0 when there is no such class.
 *
 * 0 rather than a guessed default: an unparseable width UNDER-reserves, so its
 * column squeezes visibly, where a guess would over-reserve and hide behind a
 * scrollbar. Fail towards the visible wrong answer.
 */
function widthPx(className: string | undefined): number {
  const match = className === undefined ? null : /\[(\d+)px\]/.exec(className);
  // `?? 0` for `noUncheckedIndexedAccess`, which types a matched group as
  // possibly absent even though the pattern guarantees it.
  return match === null ? 0 : Number(match[1] ?? 0);
}

const ACTIONS_WIDTH_PX = widthPx(FUTURE_ACTIONS_WIDTH);

/**
 * What the table must be at least as wide as, for the columns actually showing.
 *
 * ⚠️ **The actions lane is added unconditionally because it is LOCKED** — it can
 * never be hidden, so it is never absent from the sum.
 */
function tableMinWidth(visibleKeys: readonly string[]): number {
  return visibleKeys.reduce((total, key) => total + widthPx(FUTURE_WIDTHS[key]), ACTIONS_WIDTH_PX);
}

/* -------------------------------------------------------------------------
 * Skeletons
 * --------------------------------------------------------------------- */

/**
 * Everything the three table renderers need to agree about the column set.
 *
 * ⚠️ **ONE OBJECT RATHER THAN THREE PROPS, so the head, the body and the
 * skeleton cannot be given different answers.** A skeleton drawn over a
 * different column count from the header it sits under does not error — the
 * table simply reflows when the rows arrive, which reads as a slow page rather
 * than as a bug.
 *
 * `keys` is the DATA columns only; the actions lane is appended by each
 * renderer, because it is not a `ColumnSpec` and never takes a funnel.
 */
interface TrackerColumns {
  keys: readonly string[];
  frozenSideOf: (key: string) => FrozenSide | undefined;
  offsets: Record<string, number>;
}

/**
 * The frozen props for one cell, or nothing at all when it is not frozen.
 *
 * ⚠️ **An unfrozen cell must carry NO `frozen` and NO `frozenOffset`** — `TD`
 * only emits a `left`/`right` when `frozen` is set, and a positioned cell paints
 * over the table's collapsed borders for no reason. Spread, so the absent case
 * is genuinely absent rather than `undefined`.
 */
function frozenPropsOf(
  columns: TrackerColumns,
  key: string,
): { frozen?: FrozenSide; frozenOffset?: number } {
  const frozen = columns.frozenSideOf(key);
  if (frozen === undefined) return {};
  return { frozen, frozenOffset: columns.offsets[key] ?? 0 };
}

function TableSkeleton({ columns }: { columns: TrackerColumns }) {
  return (
    <TBody>
      {Array.from({ length: SKELETON_ROWS }, (_, rowIndex) => (
        <TR key={rowIndex}>
          {columns.keys.map((key) => (
            <TD key={key} {...frozenPropsOf(columns, key)}>
              <Skeleton className="h-3 w-24" />
            </TD>
          ))}
          <TD numeric pinned>
            <Skeleton className="ml-auto h-6 w-24" />
          </TD>
        </TR>
      ))}
    </TBody>
  );
}

function ListSkeleton() {
  return (
    <ul className="m-0 list-none p-0">
      {Array.from({ length: SKELETON_ROWS }, (_, i) => (
        <li key={i} className="border-b border-border px-4 py-3 last:border-b-0">
          <Skeleton className="h-3.5 w-44" />
          <div className="mt-2 flex items-center gap-2">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-24" />
          </div>
          <div className="mt-2.5 grid gap-1.5">
            <Skeleton className="h-2.5 w-40" />
            <Skeleton className="h-2.5 w-32" />
          </div>
        </li>
      ))}
    </ul>
  );
}

/* -------------------------------------------------------------------------
 * Page
 * --------------------------------------------------------------------- */

export function FutureBillablesTrackerPage() {
  const { ability } = useAuth();

  /*
   * The view-control permission. A hidden sidebar row is an optimisation and
   * never a guarantee — the URL is typeable, and a permission set can change
   * under an access token that outlives the change — so the page answers for
   * itself.
   *
   * ⚠️ **`trackers-invoice` IS THE SLUG AND IT DOES NOT MOVE.** The module's
   * DISPLAY NAME is being changed to "Future Billables Tracker" server-side to
   * match this page; the slug is migration 108's and is a join key that resolves
   * the seeded permission sets. Spelling a new one here would be a second module
   * that no set grants and no administrator can see.
   *
   * ⚠️ This DEGRADES OPEN — see `NAV_SURFACE_MODULE` in `@/lib/permissions`.
   * Only an explicit `none` scope denies; an unseeded catalogue, an older API or
   * a failed `/permissions/me` all leave the page visible.
   *
   * ⚠️ **Hiding a page is not refusing a request.** `GET /invoice-tracker`
   * answers the same to everybody, so nothing here may be worded as though the
   * data were protected.
   *
   * ⚠️ Computed here rather than as an early return, because the return has to
   * sit below every hook.
   */
  const canReadSurface = ability.canRead(NAV_SURFACE_MODULE.trackerInvoice);

  /*
   * ⚠️ `canUpdate('invoices')` for the add button — the SAME gate
   * `useTrackerRowWriter` puts on every inline editor, not `canCreate`. The two
   * resolve to the same WRITE_ROLES answer for this module today, and they can
   * only part company if a permission set grants one and withholds the other:
   * this tracker is one overlay with one write guard, so it reads one key.
   * DELETING is the exception and is gated separately — see
   * `DeletePlannedAction`, which is admin-only because the route is.
   */
  // Operations may add future billables too — see `canWriteFutureBillables`.
  const canAddRow = ability.canWriteFutureBillables;
  const addRowReason = futureBillablesDenialReason(ability);

  const [searchParams, setSearchParams] = useSearchParams();

  // Saved filters, against the EMERGE account rather than this browser.
  const applySavedFilters = useSavedFilterApply<SavedTrackerKey>();
  useSavedFilters({
    scope: 'future-billables-tracker',
    keys: SAVED_FILTER_KEYS,
    store: preferencesStore,
    apply: applySavedFilters,
  });

  const [result, setResult] = useState<Paginated<InvoiceTrackerRow> | null>(null);
  const [search, setSearch] = useRememberedState('search', '');
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showMoreFilters, setShowMoreFilters] = useRememberedState(
    'future-billables-more-filters',
    false,
  );
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  /** Open with no row: plan a new document. */
  const [addOpen, setAddOpen] = useState(false);
  /**
   * The one row whose edit form is open. `null` when none is.
   *
   * ⚠️ **ONE STATE AND ONE DIALOG MOUNT, NOT ONE PER ROW.** A dialog per row
   * would put N forms in the tree — each with its own company read and its own
   * copy of a basket — and would make "only one form open at a time" a thing
   * every button had to remember rather than a property of the page.
   *
   * ⚠️ **The row is read by the dialog ONCE, on opening**, so this must go
   * through `null` before it can hold a different row. Nothing here swaps it
   * while open: the only writer is the Edit button, which is disabled while this
   * is non-null.
   */
  const [editingRow, setEditingRow] = useState<InvoiceTrackerRow | null>(null);
  /** A refused Complete / Reopen, shown above the list. */
  const [actionError, setActionError] = useState<string | null>(null);
  /**
   * The tab counts. `null` while unread or after a failed read, which draws no
   * badge — an unread count is not a zero. Re-read after every write that can
   * move a row between tabs or add or remove one (`refreshCounts`).
   */
  const [planCounts, setPlanCounts] = useState<InvoiceTrackerPlanCounts | null>(null);
  const [countsToken, setCountsToken] = useState(0);
  const refreshCounts = useCallback(() => setCountsToken((token) => token + 1), []);
  useEffect(() => {
    if (!canReadSurface) return undefined;
    const controller = new AbortController();
    api
      .getFutureBillablePlanCounts(controller.signal)
      .then((response) => {
        if (!controller.signal.aborted) setPlanCounts(response.data);
      })
      .catch(() => {
        if (!controller.signal.aborted) setPlanCounts(null);
      });
    return () => controller.abort();
  }, [canReadSurface, countsToken]);

  /*
   * Filters live in the URL so a narrowed tracker survives a refresh and can be
   * linked to. Every write is `replace: true` — changing a filter is not a
   * navigation, and a history entry per keystroke would make Back mean "undo one
   * filter".
   *
   * `useSearchParams()` takes NO argument: its first parameter is a default
   * init, not options, so `useSearchParams({ replace: true })` would write a
   * literal `replace=true` into the query string and then into these filters.
   */
  const searchParamsKey = searchParams.toString();
  const params = useMemo(() => new URLSearchParams(searchParamsKey), [searchParamsKey]);
  const filters = useMemo(() => readFilters(params), [params]);
  const plan = readPlan(params);
  const activeFilterCount = FILTER_KEYS.filter((key) => filters[key] !== '').length;

  // Keyed on `result` so the identity is stable between renders: `?? []` is a
  // fresh array every time, which would make every memo below it do nothing.
  const rows = useMemo(() => result?.data ?? [], [result]);
  const total = result?.meta.total ?? 0;
  // `meta.totalPages` is 0 (not 1) for an empty set, which would disable both
  // pager buttons and render "Page 1 of 0".
  const totalPages = Math.max(result?.meta.totalPages ?? 1, 1);
  const firstRow = total === 0 ? 0 : (page - 1) * PER_PAGE + 1;
  const lastRow = total === 0 ? 0 : firstRow + rows.length - 1;
  const initialLoad = loading && !result;

  /*
   * Column filters. Sorting is the half that reaches the server; the value
   * checkboxes narrow the LOADED PAGE only, which is what each popover states.
   * `table.serverParams` is deliberately not spread into the request: no column
   * here is `scope: 'server'`, so it is always empty, and merging it would give
   * the bank above and the headers two writers for one query param.
   */
  const table = useTableFilters(rows, ALL_COLUMNS);
  const sort = asSortKey(table.sort?.key);
  /*
   * ASC by default, because the default sort is by company name and a directory
   * reads A–Z. The retired tracker defaulted DESC because it opened on a date.
   */
  const order: ListOrder = table.sort?.direction ?? 'asc';
  const visibleRows = table.visibleRows;
  const hiddenByColumns = rows.length - visibleRows.length;

  /*
   * Column layout — order, hidden set and the two frozen edges, stored AGAINST
   * THE ACCOUNT rather than in this browser. Read `use-column-layout.ts`'s
   * header before changing any of it: the HIDDEN ids are stored rather than the
   * visible ones, so a column this page gains later cannot arrive pre-hidden for
   * everybody who ever touched the control; and a failed read degrades to the
   * default layout rather than blocking the table, because the rows are the page
   * and the arrangement is chrome.
   *
   * ⚠️ **Separate from `useTableFilters` on purpose.** That hook answers WHICH
   * ROWS and belongs in the URL; this one answers which columns the reader keeps
   * and belongs on the account. Merging them would put one person's hidden
   * columns into every link they shared.
   */
  const pendingLayout = useColumnLayout(COLUMN_SCOPE, LAYOUT_KEYS, LOCKED_KEYS, PINNED_KEYS);
  /*
   * The Completed tab's own layout, under its own scope: it draws two columns
   * Pending does not, and one stored layout over two column sets would have the
   * manager offering columns its tab never draws. Both hooks always run.
   */
  const completedLayout = useColumnLayout(
    COMPLETED_COLUMN_SCOPE,
    COMPLETED_LAYOUT_KEYS,
    LOCKED_KEYS,
    PINNED_KEYS,
  );
  const columnLayout = plan === 'completed' ? completedLayout : pendingLayout;
  const filterSheetKeys = plan === 'completed' ? COMPLETED_COLUMN_KEYS : FUTURE_COLUMN_KEYS;

  /*
   * The DATA columns actually showing, and then the full cell order with the
   * actions lane forced last.
   *
   * ⚠️ **THE LANE IS RE-APPENDED RATHER THAN TRUSTED TO STAY PUT.** `moveTo`
   * refuses to move a LOCKED key, but nothing stops another column being moved
   * PAST it — `moveTo('kind', 9)` is a legal request that would leave the stored
   * order ending in something other than `actions`. The renderers always draw
   * the lane last, so the measured key list has to say the same thing or every
   * offset is a column out.
   */
  const dataColumnKeys = useMemo(
    () => columnLayout.visibleKeys.filter((key) => key !== ACTIONS_KEY),
    [columnLayout.visibleKeys],
  );
  const measuredColumnKeys = useMemo(() => [...dataColumnKeys, ACTIONS_KEY], [dataColumnKeys]);

  /**
   * ⚠️ **THE MEASURER IS TOLD THE ACTIONS LANE IS RIGHT-FROZEN; THE CELLS ARE
   * NOT.** `useFrozenOffsets` sums the rendered widths of the frozen cells
   * OUTSIDE each one, and it only walks columns its side map calls frozen — so
   * leaving the lane out of that sum would hand a second right-frozen column an
   * offset of **zero** and park it directly underneath the row buttons. Nothing
   * would error; two frozen columns would simply overlap. The cells themselves
   * keep `pinned`, which writes the same right-edge stickiness at offset 0.
   *
   * ⚠️ *SUPERSEDED: a local `frozenSideOf` wrapper that answered `'right'` for
   * `ACTIONS_KEY` and delegated everything else.* **`useFrozenOffsets` takes
   * `pinnedKeys` now** and does exactly that itself, so the wrapper — and the
   * care needed to keep the MEASURER's map and the RENDERERS' map from being
   * confused for each other — is gone. Both now read the controller's own
   * `frozenSideOf`, which never answers for the lane at all.
   *
   * ⚠️ The dependency below is the controller's own `frozenSideOf`, not the
   * controller — that function is memoised on the two freeze lists, and
   * `useFrozenOffsets` tears down and reattaches its `ResizeObserver` whenever
   * its side map changes identity.
   */
  const frozenSideOf = columnLayout.frozenSideOf;

  const headRowRef = useRef<HTMLTableRowElement | null>(null);
  const frozenOffsets = useFrozenOffsets(
    headRowRef,
    measuredColumnKeys,
    frozenSideOf,
    PINNED_KEYS,
  );

  /*
   * ⚠️ **The lane is not in this map at all.** `setFrozen` refuses a pinned key,
   * so the stored freeze lists can never name it and `frozenSideOf` can never
   * answer `'right'` for it — the lane's edge comes from `pinned` on its cells
   * and from `PINNED_KEYS` in the measurement, never from here. `keys` holds the
   * data columns only, for the same reason.
   */
  const tableColumns: TrackerColumns = useMemo(
    () => ({ keys: dataColumnKeys, frozenSideOf, offsets: frozenOffsets }),
    [dataColumnKeys, frozenSideOf, frozenOffsets],
  );

  /*
   * ⛔ **THE TABLE'S FLOOR IS A NUMBER, APPLIED INLINE.** It was the literal
   * `min-w-[1608px]`; a hidden column has to take its width with it, and a
   * computed `min-w-[${n}px]` class emits no CSS at all — see `FUTURE_WIDTHS`.
   */
  const minWidth = useMemo(() => tableMinWidth(dataColumnKeys), [dataColumnKeys]);

  /*
   * A new sort re-orders the whole table, so page 4 of the old result set is
   * meaningless in the new one.
   *
   * Adjusted during render rather than in an effect: `sort` lives inside
   * `useTableFilters`, so there is no onSort event to hang it off, and an effect
   * would commit one render (and fire one request) against the stale page first.
   */
  const resetToken = `${sort}:${order}`;
  const [lastResetToken, setLastResetToken] = useState(resetToken);
  if (lastResetToken !== resetToken) {
    setLastResetToken(resetToken);
    setPage(1);
  }

  /**
   * The request, built once and shared with the export so the file and the
   * screen can never describe different row sets.
   *
   * ⚠️ `view` and `source` are in here rather than at the two call sites, which
   * is what makes it structurally impossible for the export to carry a different
   * population from the table.
   */
  const query = useMemo(
    () => ({
      view: PINNED_VIEW,
      source: PINNED_SOURCE,
      // The tab. Server side it overrides `view` / `source` (see `PLAN_PARAM`).
      plan,
      sort,
      order,
      ...(search ? { search } : {}),
      ...filterParams(filters),
    }),
    [plan, sort, order, search, filters],
  );

  const load = useCallback(
    (signal?: AbortSignal) => {
      setLoading(true);
      return api
        .listInvoiceTracker({ ...query, page, perPage: PER_PAGE }, signal)
        .then((data) => {
          setResult(data);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setError(
            caught instanceof Error ? caught.message : 'Failed to load the future billables tracker',
          );
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [query, page],
  );

  // Debounced at 250ms so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => void load(controller.signal), 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [load]);

  /*
   * Takes the record a write RETURNED and puts it in place of the one on screen.
   *
   * ⚠️ Matched on `key`, never on `id` — `key` is the identity across this whole
   * row type, and a page keyed on `id` breaks the moment the population widens
   * to rows that have none.
   *
   * A row that is no longer in state is left alone rather than appended — the
   * page has re-read since the save started, and appending would put the row
   * outside the server's own ordering.
   */
  const replaceRow = useCallback((next: InvoiceTrackerRow) => {
    setResult((current) => {
      if (!current) return current;
      const index = current.data.findIndex((row) => row.key === next.key);
      if (index === -1) return current;
      const data = current.data.slice();
      data[index] = next;
      return { ...current, data };
    });
  }, []);

  /*
   * A deleted row leaves the table at once. `total` is decremented with it so
   * the count under the table does not disagree with the rows above it until the
   * next read. `totalPages` is deliberately NOT recomputed: a page boundary that
   * moved is corrected by the next read, and guessing at it here would be a
   * second implementation of the server's paging.
   */
  const removeRow = useCallback((key: string) => {
    setResult((current) => {
      if (!current) return current;
      const data = current.data.filter((row) => row.key !== key);
      if (data.length === current.data.length) return current;
      return {
        ...current,
        data,
        meta: { ...current.meta, total: Math.max(current.meta.total - 1, 0) },
      };
    });
  }, []);

  const companies = useCompanyFilterOptions();
  const projects = useProjectFilterOptions(filters.productId);
  const products = useProductFilterOptions();
  const currencies = useCurrencyOptions();
  /*
   * `null` per scope while `/cycles` is unread, which `projectInCycleScope`
   * reads as OFFER EVERYTHING — an unreadable cycle master degrades to the list
   * this page offered before the pills existed, never to an empty one.
   */
  const cycleScopes = useCycleScopeIds();
  const [projectScope, setProjectScope] = useRememberedState<ProjectCycleScope>(
    'future-billables-project-cycle-scope',
    DEFAULT_PROJECT_CYCLE_SCOPE,
    (raw) => (raw === 'current' || raw === 'previous' ? raw : undefined),
  );
  /*
   * ⚠️ Set ONLY by the pills. It stops the URL-held project's scope overruling a
   * pill somebody chose by hand — the same guard `FutureDocumentDialog` uses.
   */
  const [projectScopeTouched, setProjectScopeTouched] = useState(false);

  /*
   * id -> name harvested from the rows currently on screen. Cheap, and it is the
   * only label source for a value the option list cannot offer — a company past
   * the first page of the picker, say.
   */
  const companyLabels = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of result?.data ?? []) {
      if (row.company) map.set(String(row.company.id), row.company.name);
    }
    return map;
  }, [result]);

  /*
   * The same harvest for projects. It matters LESS here than for companies —
   * the option list is the WHOLE catalogue rather than one page of it, so the
   * only gap it covers is an id arriving on the URL before the fetch settles —
   * but `withSelected` needs a label source either way, and a blank trigger over
   * a filtered list reads as "no filter" while the rows stay narrowed.
   */
  const projectLabels = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of result?.data ?? []) {
      if (row.project) map.set(String(row.project.id), row.project.name);
    }
    return map;
  }, [result]);

  /* The same harvest for products, off each planned row's own basket. */
  const productLabels = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of result?.data ?? []) {
      for (const line of row.products ?? []) map.set(String(line.productId), line.productName);
    }
    return map;
  }, [result]);

  /*
   * A URL-held project opens on the pill that holds it — `Previous` for a
   * closed-cycle project — until the reader touches the pills. Converges in one
   * extra render, the `FutureDocumentDialog` shape.
   */
  // A CSV now: the first held project picks the pill.
  const heldProjectIds = useMemo(() => splitCsv(filters.projectId), [filters.projectId]);
  const heldProjectScope = useMemo<ProjectCycleScope | null>(() => {
    const first = heldProjectIds[0];
    if (first === undefined) return null;
    const held = projects.rows.find((project) => String(project.id) === first);
    if (!held) return null;
    return (
      PROJECT_CYCLE_SCOPES.find((candidate) =>
        projectInCycleScope(held, cycleScopes[candidate]),
      ) ?? null
    );
  }, [heldProjectIds, projects.rows, cycleScopes]);
  if (!projectScopeTouched && heldProjectScope !== null && heldProjectScope !== projectScope) {
    setProjectScope(heldProjectScope);
  }

  /*
   * ⚠️ **THE HELD PROJECT IS PINNED INTO EVERY PILL, AND STAYS PINNED WHEN A
   * PRODUCT NARROWS IT OUT.** A `Combobox` renders its placeholder for a value
   * absent from its options, so a project the product filter excluded would
   * read as "All projects" over a still-filtered table. The filter is never
   * cleared on the reader's behalf; it is labelled instead.
   */
  const projectPicker = useMemo(() => {
    // Every held project (a CSV now) is pinned into every pill.
    const stored: ComboboxOption[] = heldProjectIds.map((held) => {
      const row = projects.rows.find((project) => String(project.id) === held);
      if (row) return projectFilterOption(row);
      const known = projects.names.get(held) ?? projectLabels.get(held);
      return known
        ? { value: held, label: known }
        : { value: held, label: `Project #${held}`, hint: 'Not in the list below' };
    });
    const byScope = projectOptionsByScope(projects.rows, cycleScopes, projectFilterOption, null);
    for (const scope of PROJECT_CYCLE_SCOPES) {
      const inScope = byScope.options[scope];
      const missing = stored.filter(
        (option) => !inScope.some((candidate) => candidate.value === option.value),
      );
      byScope.options[scope] = [...missing, ...inScope];
    }
    return byScope;
  }, [heldProjectIds, projects.rows, projects.names, projectLabels, cycleScopes]);

  /*
   * Said only once the list has settled: while it loads or after it fails, an
   * empty pill is not a fact about the data.
   */
  const projectScopeNote =
    projects.loading || projects.failed
      ? undefined
      : projectScopeFootnote(projectScope, projectPicker.counts);

  /*
   * A row leaving this tab's list — deleted, completed or reopened — also moves
   * a tab count, so the counts are re-read with it.
   */
  const rowGone = useCallback(
    (key: string) => {
      removeRow(key);
      refreshCounts();
    },
    [removeRow, refreshCounts],
  );

  const editing: RowEditing = useMemo(
    () => ({ onSaved: replaceRow, onRowGone: rowGone, onActionError: setActionError, plan }),
    [replaceRow, rowGone, plan],
  );

  // A CSV now: every held code stays offered, as `currencyOptions` does for one.
  const currencyPickerOptions = useMemo<ComboboxOption[]>(() => {
    const base = currencyOptions(currencies.currencies, null);
    if (base.length === 0) return base;
    const missing = splitCsv(filters.currency)
      .filter((code) => !base.some((option) => option.value === code))
      .map((code) => ({ value: code, label: code, hint: 'No longer offered' }));
    return [...missing, ...base];
  }, [currencies.currencies, filters.currency]);

  const kindPickerOptions = useMemo<ComboboxOption[]>(
    () => KIND_OPTIONS.map((option) => ({ value: option.value, label: option.label })),
    [],
  );

  /** The one URL writer. Everything that touches the query string goes through it. */
  const setFilter = useCallback(
    (key: FilterKey, value: string) => {
      const next = new URLSearchParams(searchParamsKey);
      if (value) next.set(key, value);
      else next.delete(key);
      setSearchParams(next, { replace: true });
      setPage(1);
    },
    [searchParamsKey, setSearchParams],
  );

  /*
   * The mirror's two addresses, from the SHARED cached reader — the same module
   * `/client-deliverables` and the sidebar use, asked for THIS tracker's sheet.
   *
   * ⚠️ **THE ARGUMENT IS NOT OPTIONAL HERE EVEN THOUGH THE PARAMETER IS.** The
   * default is `'client-deliverables'`, so omitting it would frame the CLIENT
   * DELIVERABLES spreadsheet under this page's heading — a wrong document,
   * embedded, with nothing failing and nothing on screen to say so. That is the
   * exact hazard the hook's own header records.
   */
  const trackerSheet = useTrackerSheetLink('future-billables');

  /*
   * ⚠️ **GATED ON THE PREVIEW URL, NOT ON `link`, AND NOT ON `configured`.**
   * Three states collapse to "there is nothing to embed": no mirror configured,
   * the answer not known yet, and — the one this exists for — **a server that
   * predates `spreadsheetPreviewUrl` and does not send it.** This app deploys
   * separately from the API and can be newer than it, so that last case is
   * ordinary rather than broken and must degrade to the table rather than to a
   * toggle that opens a frame pointed at nothing.
   */
  const sheetUrl = trackerSheet.loading ? null : trackerSheet.previewLink;

  /**
   * 🔴 **THE FRAME IS THE EDIT URL — owner's instruction, 2026-09-02**, the same
   * ruling both other embeds now follow: the embed is to allow editing and
   * commenting for whoever Google says may.
   *
   * ⚠️ **`?? sheetUrl` IS NOT TIDINESS.** `spreadsheetUrl` is non-optional on the
   * response and `spreadsheetPreviewUrl` is the one an older API omits, so this
   * pair reads backwards from `sheetUrl` above on purpose. **Never derive one
   * from the other by swapping `/edit` for `/preview`** — the shape of a Google
   * address is the API's fact (see `FutureBillablesSheetLink`), and a bad swap
   * does not error, it paints an empty frame that looks like a permissions
   * problem.
   */
  const sheetEmbedUrl = trackerSheet.loading ? null : (trackerSheet.link ?? sheetUrl);
  const canShowSheet = sheetUrl !== null;

  /*
   * ⚠️ **`canShowSheet` IS APPLIED ON THE READ**, so a `?display=sheet` link on a
   * box with no mirror lands on the table rather than on a blank frame beside a
   * toggle that is not rendered. The param is left in the URL untouched:
   * rewriting somebody's link on arrival would put a second writer on the query
   * string, and the answer changes on its own the day the mirror is configured.
   */
  const wantsSheet = params.get(VIEW_PARAM) === SHEET_VIEW;
  const view: TrackerView = wantsSheet && canShowSheet ? 'sheet' : 'table';

  /*
   * ⚠️ **Only a reader who ASKED for the sheet waits for the network.** The
   * table is the default and needs no answer, so an ordinary arrival renders it
   * immediately; a `?display=sheet` arrival stands in the sheet's shape instead
   * of rendering the table and swapping it a moment later.
   */
  const resolvingSheet = wantsSheet && trackerSheet.loading;

  /*
   * ⚠️ **NOT `setFilter`, and it must not become one.** `FILTER_KEYS` is what
   * `clearFilters`, `activeFilterCount` and the saved set all iterate — putting
   * the view in it would make "Clear filters" throw the reader back to the table
   * and would count the current view as a filter in the sentence above the
   * table. It is a presentation choice, not a narrowing.
   *
   * `replace: true` for the reason every write here uses it: switching view is
   * not a navigation, and a history entry per press would make Back mean "undo
   * one toggle". The SHEET is the value and the table is the key's absence.
   */
  const setView = useCallback(
    (next: TrackerView) => {
      const nextParams = new URLSearchParams(searchParamsKey);
      if (next === 'sheet') nextParams.set(VIEW_PARAM, SHEET_VIEW);
      else nextParams.delete(VIEW_PARAM);
      setSearchParams(nextParams, { replace: true });
    },
    [searchParamsKey, setSearchParams],
  );

  /*
   * The tab switch: `replace: true` like every write here; Pending is the key's
   * absence. It resets the page and clears its own state — the column filters
   * (which narrow the loaded page only) and any refusal from the other tab.
   * The URL filters and the search are kept: they apply to both tabs.
   */
  function setPlan(next: InvoiceTrackerPlan) {
    if (next === plan) return;
    const nextParams = new URLSearchParams(searchParamsKey);
    if (next === COMPLETED_PLAN) nextParams.set(PLAN_PARAM, COMPLETED_PLAN);
    else nextParams.delete(PLAN_PARAM);
    setSearchParams(nextParams, { replace: true });
    setPage(1);
    setActionError(null);
    table.clearAll();
  }

  /* Every key this page owns, in ONE write — see `setFilter`. */
  const clearFilters = useCallback(() => {
    const next = new URLSearchParams(searchParamsKey);
    for (const key of FILTER_KEYS) next.delete(key);
    setSearchParams(next, { replace: true });
    setPage(1);
  }, [searchParamsKey, setSearchParams]);

  function clearEverything() {
    setSearch('');
    table.clearAll();
    clearFilters();
  }

  /**
   * Exports EXACTLY what the table is showing — the same `query` object the list
   * request is built from, minus the paging.
   *
   * ⚠️ Paging is dropped deliberately: an export of "page 3 of the filtered
   * tracker" is a file nobody asked for and cannot tell they got. The button's
   * label and the line beside it both say the filters travel with it, because a
   * spreadsheet that quietly holds more rows than the screen is the kind of
   * mistake somebody only finds after sending it on.
   */
  async function runExport() {
    setExporting(true);
    setExportError(null);
    try {
      const file = await api.exportInvoiceTracker(query);
      saveBlob(file.filename, file.blob);
    } catch (caught) {
      setExportError(
        caught instanceof Error ? caught.message : 'The export could not be produced.',
      );
    } finally {
      setExporting(false);
    }
  }

  const filtered = activeFilterCount > 0;
  /*
   * The SERVER's total, not "on this page": `view` and `source` are pinned on
   * the request, so `meta.total` counts exactly the rows this page shows.
   */
  const countLabel = initialLoad
    ? 'Loading…'
    : `${total} ${plan === 'completed' ? 'completed' : 'planned'} ${total === 1 ? 'document' : 'documents'}${
        search ? ` · matching “${search}”` : ''
      }${filtered ? ` · ${activeFilterCount} ${activeFilterCount === 1 ? 'filter' : 'filters'}` : ''}`;

  /*
   * ⚠️ Below every hook. The refusal is rendered here rather than by the route,
   * which carries no guard — every signed-in route in this app is an ordinary
   * `Shell` route, so a reader arriving by URL must get a sentence rather than an
   * error or a blank screen.
   */
  if (!canReadSurface) {
    return (
      <>
        <PageHeader eyebrow="Trackers" title="Future Billables Tracker" />
        <Panel className="min-w-0">
          <EmptyState
            icon={<FutureIcon />}
            title="This tracker isn’t available to your account"
            description="Your permission set does not include this. An administrator can change that."
          />
        </Panel>
      </>
    );
  }

  const emptyState = (
    <EmptyStateSlot
      search={search}
      filtered={filtered}
      // The server returned rows and the column filters hid every one of them —
      // a different problem from "nothing matched", with a different fix.
      columnsHidAll={rows.length > 0 && visibleRows.length === 0}
      onClearColumns={table.clearAll}
      onClear={clearEverything}
      hasError={Boolean(error)}
      plan={plan}
    />
  );

  return (
    <>
      <PageHeader
        eyebrow="Trackers"
        title="Future Billables Tracker"
        actions={
          <>
            {/*
             * ⚠️ **Rendered only when there is a second view to reach.** On a box
             * with no mirror the header is exactly what it was — a lone "Table"
             * chip with nothing to switch to is an affordance whose only outcome
             * is nothing happening, which is the same rule that keeps Xero's
             * `sync` button off the quote panel.
             *
             * ⚠️ **It is also the way OUT of the sheet, and that is why it stays
             * on BOTH views at every width.** A frame that paints nothing for a
             * reader with no Google access is undetectable from this side — a
             * cross-origin iframe reports no error — so the Table button is the
             * only recovery, and it has to be there without anything having
             * noticed the failure.
             */}
            {canShowSheet && <ViewToggle view={view} onChange={setView} />}
            {/*
             * ⚠️ **It adds a PLANNED ROW, and the label says so.** Nothing on
             * this page creates a quote or an invoice; the control that turns a
             * plan into a real document lives on the Accounts dashboard, and the
             * two must never be mistaken for one another.
             *
             * Rendered disabled rather than hidden when the account may not
             * write, matching the row actions and the inline editors — an absent
             * control is indistinguishable from a removed feature.
             */}
            <Button
              size="sm"
              variant="secondary"
              leadingIcon={<PlusIcon />}
              disabled={!canAddRow}
              {...(canAddRow
                ? {}
                : { title: addRowReason ?? 'You do not have permission to plan documents.' })}
              onClick={() => setAddOpen(true)}
            >
              Plan a document
            </Button>
            {/* 2026-10-02 (owner): exporting is `trackers-invoice.export`. */}
            {ability.canExport('trackers-invoice') && (
              <Button
                size="sm"
                variant="secondary"
                leadingIcon={<DownloadIcon />}
                loading={exporting}
                onClick={() => void runExport()}
              >
                Export these rows
              </Button>
            )}
          </>
        }
      />

      {/*
       * Pending / Completed (owner 2026-10-27). Over the TABLE only: the sheet
       * mirror holds every plan row with its own Status column, so the tabs
       * would switch nothing there.
       */}
      {!resolvingSheet && view === 'table' && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <PlanTabs value={plan} onChange={setPlan} counts={planCounts} />
        </div>
      )}

      {/*
       * ⚠️ **The table `Panel` below is deliberately LEFT AT ITS ORIGINAL
       * INDENTATION rather than re-indented into this branch.** Re-indenting it
       * would rewrite ~300 untouched lines and bury a small change in a diff
       * nobody can review — and this repo has mixed line endings with no
       * per-directory rule, so a bulk reflow is exactly the operation most
       * likely to convert them silently. It is not a mistake; do not "tidy" it.
       *
       * ⚠️ `sheetEmbedUrl !== null` is re-tested even though `view` can only be
       * `'sheet'` when `sheetUrl` is non-null: it is what NARROWS the type for
       * `embedUrl`, so the guard is structural rather than defensive.
       *
       * ⚠️ The placeholder is tested FIRST, so the table below is what a settled
       * "no mirror" answer falls to — never what an unsettled one flashes
       * through on the way to the frame.
       *
       * ⚠️ **No `sort` prop is passed, and its absence is a decision.** That
       * control is `GET | PUT /client-deliverables/sheet/sort`, which is that
       * mirror's setting and no other; there is no `/future-billables/sheet/sort`
       * route to read or write (the backend module is a link and a sync, nothing
       * else). `TrackerSheetPanel` renders the dropdown only when the prop is
       * given, so omitting it is what keeps a control that could change nothing
       * off this page — the rule the panel's own header records at length.
       */}
      {resolvingSheet ? (
        <SheetPlaceholderPanel />
      ) : view === 'sheet' && sheetEmbedUrl !== null ? (
        <TrackerSheetPanel
          embedUrl={sheetEmbedUrl}
          openUrl={trackerSheet.link}
          /*
           * ⚠️ **Named for THIS tracker, not left at the default.** The prop
           * defaults to "Client Deliverables tracker in Google Sheets" — the
           * accessible name of the frame — so leaving it out would announce the
           * wrong document to a screen reader while showing the right one.
           */
          frameTitle="Future Billables Tracker in Google Sheets"
        />
      ) : (
      <Panel>
        <PanelHeader title="Planned quotes and invoices" description={countLabel} />

        <div className="border-b border-border px-4 py-3">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="Search">
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  type="search"
                  placeholder="Search…"
                  leadingIcon={<SearchIcon />}
                  aria-describedby={describedBy}
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                />
              )}
            </Field>

            <MultiFilterField
              label="Company name"
              value={filters.companyId}
              onChange={(value) => setFilter('companyId', value)}
              options={withSelectedMany(
                companies.options,
                filters.companyId,
                companyLabels,
                'Company',
              )}
              allLabel="All companies"
              loading={companies.loading}
              failed={companies.failed}
              searchPlaceholder="Search companies…"
              noun={{ singular: 'company', plural: 'companies' }}
            />

            <MultiFilterField
              label="Type"
              value={filters.kind}
              onChange={(value) => setFilter('kind', value)}
              options={kindPickerOptions}
              allLabel="Quotes and invoices"
              loading={false}
              failed={false}
              noun={{ singular: 'type', plural: 'types' }}
            />

            <div className="flex items-end gap-2">
              <Button
                variant="secondary"
                leadingIcon={<FilterIcon />}
                aria-expanded={showMoreFilters}
                aria-controls="future-billables-more-filters"
                onClick={() => setShowMoreFilters((open) => !open)}
                className="w-full sm:w-auto"
              >
                {showMoreFilters ? 'Fewer filters' : 'More filters'}
              </Button>
              {/*
                * ⚠️ **WIDE SCREENS ONLY, because below `lg` there is no table for
                * it to arrange** — the rows are a stacked card list, which has no
                * columns to hide, reorder or freeze. A control whose only outcome
                * is nothing is the rule that keeps Xero's `sync` button off the
                * quote panel, and it applies per breakpoint here.
                *
                * ⚠️ **`lg:contents` on a span rather than `hidden lg:inline-flex`
                * on the button.** `Button` bakes in its own `display`, and two
                * utilities for one property are settled by the emitted
                * stylesheet's order rather than by the order they are written in.
                * The span makes the visibility a property of a wrapper that
                * declares nothing else, so there is nothing to race — the same
                * idiom "Clear column filters" already uses below.
                */}
              <span className="hidden lg:contents">
                <ColumnManager
                  layout={columnLayout}
                  labelOf={columnLabelOf}
                  lockedKeys={LOCKED_KEYS}
                  lockReasonOf={columnLockReasonOf}
                  pinnedKeys={PINNED_KEYS}
                />
              </span>
            </div>
          </div>

          {showMoreFilters && (
            <div
              id="future-billables-more-filters"
              className="mt-3 grid gap-3 border-t border-border pt-3 sm:grid-cols-2 lg:grid-cols-3"
            >
              {/*
                * ⚠️ **HERE RATHER THAN THE TOP BANK, and that is a layout fact
                * rather than a ranking.** The bank above is `lg:grid-cols-4`
                * whose fourth cell is the button pair, so a fifth control there
                * would push More filters and the column manager onto a second
                * row at every width. This grid is `lg:grid-cols-3` and wraps
                * cleanly.
                *
                * ⚠️ **No `onSearch`**: the option list is the WHOLE project
                * catalogue (or every project selling the chosen product), so
                * `Combobox`'s own local search is exhaustive — within the pill.
                * The only footnote is the pills' fact about an empty pill. See
                * `useProjectFilterOptions`.
                *
                * The Current / Previous pills sit ABOVE the picker and outside
                * its `Field`, the shape every other mount of this control uses:
                * they choose which cycles the list OFFERS.
                */}
              <div className="grid min-w-0 content-start gap-1.5">
                <SegmentedControl
                  label="Project cycle"
                  value={projectScope}
                  options={PROJECT_CYCLE_SCOPE_OPTIONS}
                  onChange={(next) => {
                    setProjectScope(next);
                    setProjectScopeTouched(true);
                  }}
                  disabled={projects.failed}
                />
                <MultiFilterField
                  label="Project"
                  value={filters.projectId}
                  onChange={(value) => setFilter('projectId', value)}
                  options={projectPicker.options[projectScope]}
                  allLabel="All projects"
                  loading={projects.loading}
                  failed={projects.failed}
                  searchPlaceholder="Search projects…"
                  noun={{ singular: 'project', plural: 'projects' }}
                  {...(projectScopeNote ? { footnote: projectScopeNote } : {})}
                />
              </div>

              {/*
                * Every ACTIVE product, paged in whole. Choosing ONE also narrows
                * the Project list beside it to projects that sell it.
                */}
              <MultiFilterField
                label="Product"
                value={filters.productId}
                onChange={(value) => setFilter('productId', value)}
                options={withSelectedMany(
                  products.options,
                  filters.productId,
                  productLabels,
                  'Product',
                )}
                allLabel="All products"
                loading={products.loading}
                failed={products.failed}
                searchPlaceholder="Search products…"
                noun={{ singular: 'product', plural: 'products' }}
              />

              <MultiFilterField
                label="Currency"
                value={filters.currency}
                onChange={(value) => setFilter('currency', value)}
                options={currencyPickerOptions}
                allLabel="Any currency"
                loading={currencies.loading}
                failed={currencies.failed}
                searchPlaceholder="Search currencies…"
                noun={{ singular: 'currency', plural: 'currencies' }}
              />

              {/*
                * The two amount bounds write to the URL on every keystroke, and
                * `readFilters` drops anything that does not parse — so a
                * half-typed figure narrows nothing rather than 422ing the list.
                */}
              <div className="grid grid-cols-2 gap-3">
                <TextFilterField
                  label="Amount from"
                  inputMode="decimal"
                  value={filters.amountMin}
                  onChange={(value) => setFilter('amountMin', value)}
                />
                <TextFilterField
                  label="Amount to"
                  inputMode="decimal"
                  value={filters.amountMax}
                  onChange={(value) => setFilter('amountMax', value)}
                />
              </div>

              {/*
                * ⚠️ **The 30-day window is the SERVER'S.** A browser-side date
                * comparison would be a second definition of "the next 30 days"
                * and would disagree with the Accounts dashboard's own figure on
                * the boundary day, with nothing on screen saying so.
                */}
              <div className="flex items-end">
                <Button
                  variant={filters.plannedIn30Days ? 'secondary' : 'ghost'}
                  aria-pressed={Boolean(filters.plannedIn30Days)}
                  className="w-full sm:w-auto"
                  onClick={() =>
                    setFilter('plannedIn30Days', filters.plannedIn30Days ? '' : FLAG_ON)
                  }
                >
                  Due in the next 30 days
                </Button>
              </div>
            </div>
          )}

          {filtered && (
            <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3">
              <p className="m-0 text-xs text-fg-muted">
                {activeFilterCount} {activeFilterCount === 1 ? 'filter' : 'filters'} applied.
              </p>
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Clear filters
              </Button>
            </div>
          )}

          {/*
            * Explained here, not only at the headers, so a filter set at one
            * breakpoint doesn't silently keep hiding rows at another. The clear
            * button is wide-screen only — below `lg` the FilterSheet has its own,
            * and two buttons with the same label a screen-length apart would read
            * as two different scopes.
            */}
          {table.activeCount > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3">
              <p className="m-0 text-xs text-fg-muted">
                {table.activeCount} column {table.activeCount === 1 ? 'filter' : 'filters'} applied
                to the {rows.length} {rows.length === 1 ? 'row' : 'rows'} on this page
                {hiddenByColumns > 0 ? `, hiding ${hiddenByColumns}` : ''}.
              </p>
              <span className="hidden lg:contents">
                <Button variant="ghost" size="sm" onClick={table.clearAll}>
                  Clear column filters
                </Button>
              </span>
            </div>
          )}
        </div>

        <p role="status" aria-live="polite" className="sr-only">
          {countLabel}
        </p>

        {error && (
          <div className="border-b border-border px-4 py-3">
            <Notice
              tone="danger"
              role="alert"
              title={<>Couldn&rsquo;t load the future billables tracker.</>}
            >
              <span className="[overflow-wrap:anywhere]">{error}</span>
            </Notice>
          </div>
        )}

        {actionError && (
          <div className="border-b border-border px-4 py-3">
            <Notice
              tone="danger"
              role="alert"
              title={
                plan === 'completed' ? (
                  <>The row wasn&rsquo;t reopened.</>
                ) : (
                  <>The row wasn&rsquo;t marked Completed.</>
                )
              }
            >
              <span className="[overflow-wrap:anywhere]">{actionError}</span>
            </Notice>
          </div>
        )}

        {exportError && (
          <div className="border-b border-border px-4 py-3">
            <Notice tone="danger" role="alert" title={<>The export didn&rsquo;t download.</>}>
              <span className="[overflow-wrap:anywhere]">{exportError}</span>
            </Notice>
          </div>
        )}

        <div
          aria-busy={loading || undefined}
          className={cx(
            'transition-opacity duration-150 ease-out motion-reduce:transition-none',
            loading && result && 'opacity-60',
          )}
        >
          {/* Below lg an eight-column table cannot fit 320px, so rows become a stacked list. */}
          <div className="lg:hidden">
            {/*
              * `FilterSheet` is the header row's mobile equivalent — cards have no
              * header row for the column funnels to hang off below `lg`. It shares
              * the same `headerFor`/`clearAll`, so a filter set here is what the
              * table shows once the viewport widens (one state store).
              */}
            <div className="border-b border-border px-4 py-2">
              <FilterSheet
                keys={filterSheetKeys}
                headerFor={table.headerFor}
                activeCount={table.activeCount}
                clearAll={table.clearAll}
              />
            </div>
            {initialLoad ? (
              <ListSkeleton />
            ) : visibleRows.length === 0 ? (
              emptyState
            ) : (
              <ul className="m-0 list-none p-0">
                {visibleRows.map((row) => (
                  <RowCard
                    key={row.key}
                    row={row}
                    editing={editing}
                    formOpen={addOpen || editingRow !== null}
                    onEdit={() => setEditingRow(row)}
                  />
                ))}
              </ul>
            )}
          </div>

          <div className="hidden lg:block">
            {initialLoad ? (
              <TableWrap frozenHeader>
                {/*
                  * ⚠️ *SUPERSEDED: "The floor sits on a WRAPPER, not on `Table`,
                  * which takes only `className`."* **`Table` takes a `style`
                  * now**, so the spacer `<div>` is gone and the floor sits on the
                  * element it describes. The value is computed, so it still has
                  * to reach the DOM as a style rather than as a class — a
                  * `min-w-[${n}px]` emits no CSS at all and looks applied.
                  */}
                <Table style={{ minWidth }}>
                  <TrackerTableHead
                    headerFor={table.headerFor}
                    columns={tableColumns}
                    rowRef={headRowRef}
                  />
                  <TableSkeleton columns={tableColumns} />
                </Table>
              </TableWrap>
            ) : visibleRows.length === 0 ? (
              emptyState
            ) : (
              <TableWrap frozenHeader>
                <Table style={{ minWidth }}>
                  <TrackerTableHead
                    headerFor={table.headerFor}
                    columns={tableColumns}
                    rowRef={headRowRef}
                  />
                  <TBody>
                    {visibleRows.map((row) => (
                      <RowCells
                        key={row.key}
                        row={row}
                        editing={editing}
                        formOpen={addOpen || editingRow !== null}
                        onEdit={() => setEditingRow(row)}
                        columns={tableColumns}
                      />
                    ))}
                  </TBody>
                </Table>
              </TableWrap>
            )}
          </div>
        </div>

        {result && (
          <PanelFooter>
            <p className="m-0 text-center text-xs text-fg-muted tabular sm:text-left">
              {total === 0 ? 'No rows to show' : `Showing ${firstRow}–${lastRow} of ${total}`}
              {hiddenByColumns > 0 ? ` · ${hiddenByColumns} hidden by column filters` : ''}
            </p>
            <div className="flex items-center gap-2">
              <span className="hidden text-xs text-fg-subtle tabular sm:inline">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                className="flex-1 sm:flex-none"
                leadingIcon={<ChevronIcon direction="left" />}
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                className="flex-1 sm:flex-none"
                trailingIcon={<ChevronIcon direction="right" />}
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </PanelFooter>
        )}
      </Panel>
      )}

      {/*
       * ⚠️ **TWO MOUNTS OF ONE DIALOG, AND THEY MUST NOT BE COLLAPSED INTO ONE.**
       * `FutureDocumentDialog` reads its `row` prop ONCE, at mount, and unmounts
       * itself while closed — so a single mount whose `row` flipped between
       * `undefined` and a record would seed a create form with an edit's values,
       * or the reverse, silently. Two mounts, two independent open flags.
       *
       * ⚠️ **`onCreated` RE-READS rather than splicing the row in.** Where it
       * belongs is the server's answer: this list is sorted, paginated and
       * narrowed by up to five filters, so a row inserted locally would sit
       * outside the server's own ordering — and one that does not match the
       * active filters would appear on a page it has no business being on.
       * `replaceRow` exists for a record a write RETURNED for a row already on
       * screen, which is a different question.
       *
       * ⚠️ It fires after an EDIT as well as a create; the prop's name is the
       * dialog's and means "the list is out of date", which is true of both.
       */}
      <FutureDocumentDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={() => {
          setAddOpen(false);
          void load();
          refreshCounts();
        }}
      />
      {editingRow !== null && (
        <FutureDocumentDialog
          open
          row={editingRow}
          onClose={() => setEditingRow(null)}
          onCreated={() => {
            setEditingRow(null);
            void load();
            refreshCounts();
          }}
        />
      )}
    </>
  );
}

/**
 * What a row needs to be editable, threaded from the page rather than fetched
 * per row.
 */
interface RowEditing {
  /** Replaces the row in page state with the record the write returned. */
  onSaved: (next: InvoiceTrackerRow) => void;
  /** Drops a row that has left this list, by `key` (and re-reads the tab counts). */
  onRowGone: (key: string) => void;
  /** Shows (or, with `null`, clears) a refused Complete / Reopen above the list. */
  onActionError: (message: string | null) => void;
  /** Which tab the row is on — decides Complete vs Reopen and the raised link. */
  plan: InvoiceTrackerPlan;
}

/**
 * The per-row actions, defined ONCE and rendered by both the table and the card
 * list.
 *
 * ⚠️ **ONE DEFINITION BECAUSE THE TWO SURFACES MUST NOT DRIFT.** This codebase
 * has had a working feature reported as *deleted* because it was rendered only
 * on desktop, and Delete is the control somebody most needs on the row in front
 * of them.
 *
 * ⚠️ `flex-wrap` is what keeps the lane a FIXED WIDTH — without it a row
 * carrying both controls would push the lane wider, and every column left of it
 * narrower, so the table would reflow depending on which rows the pager happened
 * to land on.
 */
function RowActions({
  row,
  editing,
  formOpen,
  onEdit,
}: {
  row: InvoiceTrackerRow;
  editing: RowEditing;
  formOpen: boolean;
  onEdit: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center justify-end gap-1">
      {editing.plan === 'completed' ? (
        <>
          <RaisedDocumentLink row={row} />
          <ReopenPlannedAction
            row={row}
            disabled={formOpen}
            onRowGone={editing.onRowGone}
            onError={editing.onActionError}
          />
        </>
      ) : (
        <CompletePlannedAction
          row={row}
          disabled={formOpen}
          onRowGone={editing.onRowGone}
          onError={editing.onActionError}
        />
      )}
      <EditPlannedAction row={row} disabled={formOpen} onOpen={onEdit} />
      <DeletePlannedAction row={row} disabled={formOpen} onDeleted={editing.onRowGone} />
    </div>
  );
}

/**
 * Who the row bills, as rendered (owner demarcation 2026-10-25): a RAISED quote
 * or invoice is synced with Xero, so it names its customer by the Billing name;
 * a planned row is not in Xero yet and keeps `partyName` (the Company name or the
 * typed party).
 */
function rowPartyLabel(row: InvoiceTrackerRow): string {
  if (row.company && (row.invoiceId !== null || row.quoteId !== null)) {
    return documentCustomerName(row.company);
  }
  return row.partyName;
}

/**
 * One body cell, by column key.
 *
 * ⚠️ **EVERY `max-w` HERE MIRRORS THAT COLUMN'S ENTRY IN `FUTURE_WIDTHS`, AND IT
 * IS NOT REDUNDANT WITH IT.** The header's `w-[…]` is what the column ASKS for;
 * this is what stops a cell demanding more. The link and label cells use
 * `truncate`, which is `white-space: nowrap` — so their min-content width is the
 * WHOLE string, and without a cap a long company name would widen its column
 * past the declared width and squeeze every other one. The two have to move
 * together; a `max-w` wider than the `w-[…]` quietly re-opens that.
 *
 * ⚠️ **AN UNKNOWN KEY RETURNS `null` AND THE CALLER STILL RENDERS AN EMPTY
 * CELL.** The keys come from the stored layout, which is an unvalidated document
 * that can name a column this table no longer has — `applyOrder` already drops
 * those against the live key list, so this branch is unreachable today and is
 * the second guard rather than the first. It degrades to a BLANK COLUMN rather
 * than to a missing one on purpose: skipping the cell would put the row one cell
 * out of step with the header, and a body row shorter than its heading is the
 * one failure here that shifts every measured freeze offset by a whole column
 * without erroring. A blank column is visible and wrong; a shifted one is
 * invisible and wrong.
 */
function rowCellFor(
  key: string,
  row: InvoiceTrackerRow,
  writer: TrackerRowWriter,
): { className?: string; numeric?: boolean; content: ReactNode } | null {
  switch (key) {
    case 'kind':
      return {
        className: 'max-w-[96px] whitespace-nowrap text-xs',
        content: kindLabel(row.kind),
      };
    case 'party':
      return {
        className: 'max-w-[200px]',
        content: row.company ? (
          <TextLink
            to={`/companies/${row.company.id}`}
            title={rowPartyLabel(row)}
            className="block truncate text-sm"
          >
            {rowPartyLabel(row)}
          </TextLink>
        ) : (
          /*
           * ⚠️ **`partyName` is ALWAYS present and `company` is not.** A planned
           * row may be typed against a free-text name rather than a CRM account,
           * so this is the field to RENDER and `company` is only the field to
           * link from.
           */
          <span title={row.partyName} className="block truncate text-sm">
            {row.partyName}
          </span>
        ),
      };
    case 'services':
      return { className: 'max-w-[216px]', content: <ServicesCell services={row.services} /> };
    case 'project':
      return { className: 'max-w-[200px]', content: <ProjectCell project={row.project} /> };
    case 'currency':
      return {
        className: 'whitespace-nowrap text-xs tabular',
        content: row.currency ?? <NotRecorded />,
      };
    case 'amount':
      /*
       * Right-aligned and tabular, ALWAYS with its currency code beside it.
       * There is no exchange rate anywhere on this page, so a figure that does
       * not say which currency it is in is a figure somebody will add to another
       * one.
       */
      return {
        numeric: true,
        content: <InvoiceMoney value={row.amount} currency={row.currency} />,
      };
    case 'plannedFrom':
      return {
        className: 'max-w-[152px]',
        content: (
          <PlannedDateField
            label="Planned from"
            value={row.plannedRaiseFrom}
            requestKey="plannedRaiseFrom"
            writer={writer}
          />
        ),
      };
    case 'plannedTo':
      return {
        className: 'max-w-[144px]',
        content: (
          <PlannedDateField
            label="Planned to"
            value={row.plannedRaiseTo}
            requestKey="plannedRaiseTo"
            writer={writer}
          />
        ),
      };
    case 'comment':
      return { className: 'max-w-[240px]', content: <CommentField row={row} writer={writer} /> };
    case 'addedBy':
      return { className: 'max-w-[184px]', content: <AddedByCell person={row.createdBy} /> };
    case 'completedOn':
      return {
        className: 'max-w-[136px] whitespace-nowrap text-xs tabular',
        content: row.completedAt === null ? <NotRecorded /> : formatDateOnly(row.completedAt),
      };
    case 'completedBy':
      return {
        className: 'max-w-[160px]',
        content:
          row.completedBy === null ? (
            <NotRecorded />
          ) : (
            <span title={row.completedBy.name} className="block truncate text-sm">
              {row.completedBy.name}
            </span>
          ),
      };
    default:
      return null;
  }
}

/**
 * One table row.
 *
 * ⚠️ Keyed on `row.key` by every caller, never on `row.id`.
 *
 * ⚠️ **THE CELLS ARE ORDERED BY `columns.keys`, NOT WRITTEN OUT.** That array is
 * the same one the header and the skeleton render from, so a column hidden,
 * moved or frozen in the manager cannot leave the body describing a different
 * table from the heading above it — and, more sharply, cannot leave the two with
 * a DIFFERENT NUMBER OF CELLS, which is what shifts every measured freeze offset
 * by a whole column with nothing erroring.
 */
function RowCells({
  row,
  editing,
  formOpen,
  onEdit,
  columns,
}: {
  row: InvoiceTrackerRow;
  editing: RowEditing;
  formOpen: boolean;
  onEdit: () => void;
  columns: TrackerColumns;
}) {
  const writer = useTrackerRowWriter(row, editing.onSaved);
  return (
    <TR interactive>
      {columns.keys.map((key) => {
        const cell = rowCellFor(key, row, writer);
        return (
          <TD
            key={key}
            {...(cell?.className === undefined ? {} : { className: cell.className })}
            {...(cell?.numeric === undefined ? {} : { numeric: cell.numeric })}
            {...frozenPropsOf(columns, key)}
          >
            {cell?.content}
          </TD>
        );
      })}
      {/*
       * ⚠️ **`pinned` HERE, `frozen` NEVER.** `pinned` is `sticky right-0`, so it
       * can only ever hold the column that is actually last — which this lane
       * always is, because it is locked out of the manager's reordering. A data
       * column reaching for `pinned` would park itself on top of this one.
       */}
      <TD numeric pinned className="max-w-[132px]">
        <RowActions row={row} editing={editing} formOpen={formOpen} onEdit={onEdit} />
      </TD>
    </TR>
  );
}

/**
 * The sub-`lg` stacked list — the same rows, the same values, one column, and
 * **the same editors and the same actions**.
 *
 * A component per row rather than a `map` returning markup, because each row
 * needs its own writer. Leaving the cards read-only would make the Comment and
 * the planned dates unreachable on a phone, and this tracker has one screen.
 */
function RowCard({
  row,
  editing,
  formOpen,
  onEdit,
}: {
  row: InvoiceTrackerRow;
  editing: RowEditing;
  formOpen: boolean;
  onEdit: () => void;
}) {
  const writer = useTrackerRowWriter(row, editing.onSaved);
  return (
    <li className="border-b border-border px-4 py-3 last:border-b-0">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {row.company ? (
            <TextLink to={`/companies/${row.company.id}`} className="block truncate text-sm font-medium">
              {rowPartyLabel(row)}
            </TextLink>
          ) : (
            <p className="m-0 truncate text-sm font-medium text-fg">{row.partyName}</p>
          )}
          <p className="m-0 mt-0.5 text-xs text-fg-muted">{kindLabel(row.kind)}</p>
        </div>
        <span className="shrink-0 text-sm">
          <InvoiceMoney value={row.amount} currency={row.currency} />
        </span>
      </div>

      <dl className="mt-2.5 grid gap-1.5 text-xs">
        <div className="flex items-baseline gap-2">
          <dt className="w-28 shrink-0 text-fg-subtle">Services</dt>
          <dd className="m-0 min-w-0 flex-1">
            <ServicesCell services={row.services} />
          </dd>
        </div>
        {/*
         * ⚠️ **THE CARD CARRIES THE PROJECT TOO, AND THAT IS NOT OPTIONAL.** The
         * same rule the Added by row states below: a column that exists only
         * above `lg` is indistinguishable on a phone from one that was never
         * built, and this codebase has had a working feature reported by the
         * owner as DELETED for exactly that. It sits beside Services here for
         * the same reason it does in the table — what is being sold, and what it
         * is for.
         */}
        <div className="flex items-baseline gap-2">
          <dt className="w-28 shrink-0 text-fg-subtle">Project</dt>
          <dd className="m-0 min-w-0 flex-1">
            <ProjectCell project={row.project} />
          </dd>
        </div>
        <div className="flex items-baseline gap-2">
          <dt className="w-28 shrink-0 text-fg-subtle">Planned from</dt>
          <dd className="m-0 min-w-0 flex-1">
            <PlannedDateField
              label="Planned from"
              value={row.plannedRaiseFrom}
              requestKey="plannedRaiseFrom"
              writer={writer}
            />
          </dd>
        </div>
        <div className="flex items-baseline gap-2">
          <dt className="w-28 shrink-0 text-fg-subtle">Planned to</dt>
          <dd className="m-0 min-w-0 flex-1">
            <PlannedDateField
              label="Planned to"
              value={row.plannedRaiseTo}
              requestKey="plannedRaiseTo"
              writer={writer}
            />
          </dd>
        </div>
        <div className="flex items-baseline gap-2">
          <dt className="w-28 shrink-0 text-fg-subtle">Comment</dt>
          <dd className="m-0 min-w-0 flex-1">
            <CommentField row={row} writer={writer} />
          </dd>
        </div>
        {/*
         * ⚠️ **THE CARD CARRIES IT TOO, AND THAT IS NOT OPTIONAL.** A column
         * that exists only above `lg` is indistinguishable on a phone from one
         * that was never built — and Accounts reading this on a phone is
         * exactly the person who needs to know who to ring.
         */}
        <div className="flex items-baseline gap-2">
          <dt className="w-28 shrink-0 text-fg-subtle">Added by</dt>
          <dd className="m-0 min-w-0 flex-1">
            <AddedByCell person={row.createdBy} />
          </dd>
        </div>
        {/* The Completed tab's two columns, on the card too. */}
        {editing.plan === 'completed' && (
          <>
            <div className="flex items-baseline gap-2">
              <dt className="w-28 shrink-0 text-fg-subtle">Completed on</dt>
              <dd className="m-0 min-w-0 flex-1 tabular">
                {row.completedAt === null ? <NotRecorded /> : formatDateOnly(row.completedAt)}
              </dd>
            </div>
            <div className="flex items-baseline gap-2">
              <dt className="w-28 shrink-0 text-fg-subtle">Completed by</dt>
              <dd className="m-0 min-w-0 flex-1">
                {row.completedBy === null ? <NotRecorded /> : row.completedBy.name}
              </dd>
            </div>
          </>
        )}
      </dl>

      <div className="mt-3">
        <RowActions row={row} editing={editing} formOpen={formOpen} onEdit={onEdit} />
      </div>
    </li>
  );
}

/**
 * Every header is a `FilterableTH`, driven off the LIVE VISIBLE COLUMN SET the
 * row cells are ordered by — so a column hidden, moved or frozen in the manager
 * cannot leave the header row describing a different table from the body.
 *
 * The actions header is NOT a column: it carries no filterable value, and giving
 * it a funnel would offer to filter on a button.
 *
 * ⚠️ **`rowRef` GOES ON THIS `TR` AND NOWHERE ELSE.** `useFrozenOffsets`
 * measures `row.children` POSITIONALLY against the key array it is given, so the
 * element it is handed has to be the row whose cells are those keys, in that
 * order, with the actions cell last. Hand it any other row and every offset is
 * some other column's width — which does not error, it just overlaps two frozen
 * columns by the difference.
 *
 * ⚠️ *SUPERSEDED: "The row is a plain `<tr>`, not `TR`, and that is forced …
 * **If `TR` ever gains a `ref`, this goes back to `<TR ref={…}>`.**"* **`TR`
 * takes a `ref` now**, so it has. The copy of `TR`'s class list that stood here
 * is deleted: it was a second definition of a shared component's styling with
 * nothing to keep it in step, and a divergence would have shown up as one header
 * row missing a rule rather than as anything that fails.
 *
 * The offsets are still the summed RENDERED widths of this row's cells, which is
 * the only place those widths exist — auto layout, no `<colgroup>`, so a `w-*`
 * is a request the content may outvote.
 */
function TrackerTableHead({
  headerFor,
  columns,
  rowRef,
}: {
  headerFor: (key: string) => FilterableTHProps;
  columns: TrackerColumns;
  rowRef: RefObject<HTMLTableRowElement | null>;
}) {
  return (
    <THead>
      <TR ref={rowRef}>
        {columns.keys.map((key) => {
          const width = FUTURE_WIDTHS[key];
          /*
           * `headerFor` never returns a `className` (measured in
           * `use-table-filters.ts` — it returns `label`, `numeric` and
           * `filter`), so this assigns rather than merges. If it ever starts
           * returning one, merge with `cx` instead of overwriting it.
           *
           * ⚠️ *SUPERSEDED: a local `TrackerHeadCell`, a second renderer of this
           * component's body for the frozen case, because `FilterableTHProps`
           * had no `frozen`/`frozenOffset`.* **It forwards them to `TH` now**, so
           * the copy — and its hand-kept `aria-sort` mapping — is deleted.
           */
          return (
            <FilterableTH
              key={key}
              {...headerFor(key)}
              {...(width === undefined ? {} : { className: width })}
              {...frozenPropsOf(columns, key)}
            />
          );
        })}
        {/*
          * ⚠️ **`pinned`, never `frozen`.** The two write the same right-edge
          * stickiness and `pinned` is the one-column shorthand `Table.tsx` keeps
          * on purpose; passing both would apply two overlapping sets of sticky
          * and shadow classes and leave the emitted order to decide which rule
          * paints. The MEASURER is still told this lane occupies the right edge —
          * it is in `PINNED_KEYS`, which `useFrozenOffsets` counts — or the next
          * right-frozen column lands underneath it.
          */}
        <TH numeric pinned className={FUTURE_ACTIONS_WIDTH}>
          <span className="sr-only">Actions</span>
        </TH>
      </TR>
    </THead>
  );
}

function EmptyStateSlot({
  search,
  filtered,
  columnsHidAll,
  onClearColumns,
  onClear,
  hasError,
  plan,
}: {
  search: string;
  filtered: boolean;
  columnsHidAll: boolean;
  onClearColumns: () => void;
  onClear: () => void;
  hasError: boolean;
  plan: InvoiceTrackerPlan;
}) {
  if (hasError) {
    return (
      <EmptyState
        icon={<FutureIcon />}
        title="Nothing to show"
        description="See the message above."
      />
    );
  }

  /*
   * The server did return rows — the column filters hid all of them. Saying "no
   * rows match" here would send the reader to change the search or the filters
   * above, neither of which is the cause. The headers go with the table, so the
   * way out has to be offered here.
   */
  if (columnsHidAll) {
    return (
      <EmptyState
        icon={<FutureIcon />}
        title="Every row on this page is filtered out"
        description="Column filters apply to this page only. Clear them to see it again."
        action={
          <Button variant="secondary" size="sm" onClick={onClearColumns}>
            Clear column filters
          </Button>
        }
      />
    );
  }

  const narrowed = Boolean(search) || filtered;

  if (plan === 'completed' && !narrowed) {
    return (
      <EmptyState
        icon={<FutureIcon />}
        title="Nothing is completed"
        description="A row appears here once it is marked Completed or a quote or invoice is raised from it."
      />
    );
  }

  return (
    <EmptyState
      icon={<FutureIcon />}
      title={narrowed ? 'No rows match' : 'Nothing is planned'}
      /*
       * ⚠️ NEITHER SENTENCE MAY IMPLY DATA IS ON ITS WAY. Every row here is
       * typed by a person, so an empty tracker is an ordinary answer rather than
       * a load that has not finished — and it says where a row comes from, which
       * is the one thing a reader can act on.
       */
      description={
        narrowed
          ? 'Nothing matches these filters. Clear them to see everything that is planned.'
          : 'A row appears here for every quote or invoice somebody has planned and nobody has raised or marked Completed yet. Use “Plan a document” to add one.'
      }
      action={
        narrowed ? (
          <Button variant="secondary" size="sm" onClick={onClear}>
            Clear search and filters
          </Button>
        ) : null
      }
    />
  );
}
